import { dispositionOf, dispositionColor } from '../../../shared/creatureDisposition';
import { createPortal } from 'react-dom';
import useMapCombatShortcuts from '../utils/useMapCombatShortcuts';
import {tokenFootprintLayout, artworkCenterOffset, loadArtworkBounds} from '../utils/tokenVisualGeometry';
import useMapFog from '../hooks/useMapFog';
import useCustomTokenScales from '../hooks/useCustomTokenScales';
import useTokenRotationShortcuts from '../hooks/useTokenRotationShortcuts';
import FogOfWar, { FogControls } from '../components/FogOfWar';
import MapCanopy from '../components/MapCanopy';
import MapStickers from '../components/MapStickers';
import useMapStickerMoves from '../hooks/useMapStickerMoves';
import { revealed, tokenRevealed } from '../../../shared/fogMask.mjs';
import PersistentSpellAreas from '../components/PersistentSpellAreas';
import PaladinAuraAreas from '../components/PaladinAuraAreas';
import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import SpellAreaPreview from '../components/SpellAreaPreview';
import TargetRangePreview from '../components/TargetRangePreview';
import { mapAreaBarriers, areaTargetIds } from '../../../shared/rules/effects/spellAreaGeometry.mjs';
import classNames from '../../../utils/classNames';
import { resolveFigurineImageData } from '../utils/figurineAssets';
import resolveMapImageSource from '../utils/mapImages';
import clampMapZoom, {
  calculateAnchoredPan,
  calculateCursorAnchoredPan,
  MAP_ZOOM_DEFAULT,
} from '../utils/mapZoom';
import usePointerEventsSupported from '../../../hooks/usePointerEventsSupported';
import { enhanceMouseEvent, enhanceTouchEvent } from '../../../utils/pointerEvents';
import { getOccupiedGridCells, getOccupiedSquareSize } from '../utils/gridSpatial';

const clamp01 = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  if (parsed < 0) {
    return 0;
  }
  if (parsed > 1) {
    return 1;
  }
  return parsed;
};

const getHealthColor = (ratio) => {
  const clampedRatio = clamp01(ratio);
  if (clampedRatio === null) {
    return '#51cf66';
  }

  const hue = Math.round(120 * clampedRatio);
  return `hsl(${hue}, 70%, 45%)`;
};

const toFiniteNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatHpValue = (value) => {
  if (!Number.isFinite(value)) {
    return '0';
  }

  const clamped = value < 0 ? 0 : value;
  if (Number.isInteger(clamped)) {
    return `${clamped}`;
  }

  const precision = Math.abs(clamped) >= 100 ? 0 : 1;
  return clamped.toFixed(precision);
};

const normalizeText = (value) =>
  typeof value === 'string' && value.trim() !== '' ? value.trim() : null;

const FIGURINE_SIZE_MULTIPLIERS = {
  tiny: 0.5,
  small: 1,
  medium: 1,
  large: 2,
  huge: 3,
  gargantuan: 4,
};

const MAX_FIGURINE_GRID_SQUARES = FIGURINE_SIZE_MULTIPLIERS.gargantuan;
const DEFAULT_FIGURINE_GRID_SQUARES = FIGURINE_SIZE_MULTIPLIERS.medium;
const FALLBACK_FIGURINE_PIXEL_SQUARE_SIZE = 512;

const ROTATION_HANDLE_DISTANCE_BASE_SCALE = 1.35;
const ROTATION_HANDLE_DISTANCE_MIN_SCALE = 1.05;
const ROTATION_HANDLE_DISTANCE_REDUCTION_PER_EXTRA_SCALE = 0.15;

const resolveRotationHandleDistanceScale = (figurineScale) => {
  if (!Number.isFinite(figurineScale) || figurineScale <= 0) {
    return ROTATION_HANDLE_DISTANCE_BASE_SCALE;
  }

  const extraScale = Math.max(0, figurineScale - 1);
  const reducedScale =
    ROTATION_HANDLE_DISTANCE_BASE_SCALE -
    ROTATION_HANDLE_DISTANCE_REDUCTION_PER_EXTRA_SCALE * extraScale;

  if (reducedScale < ROTATION_HANDLE_DISTANCE_MIN_SCALE) {
    return ROTATION_HANDLE_DISTANCE_MIN_SCALE;
  }

  if (reducedScale > ROTATION_HANDLE_DISTANCE_BASE_SCALE) {
    return ROTATION_HANDLE_DISTANCE_BASE_SCALE;
  }

  return reducedScale;
};

const DEFAULT_GRID_DIMENSION = 24;
const MAP_ZOOM_EPSILON = 0.0005;
const MAP_ZOOM_ANIMATION_TIME_CONSTANT_MS = 90;

const getTouchGeometry = (touches) => {
  if (!touches || touches.length < 2) {
    return null;
  }

  const first = touches[0];
  const second = touches[1];
  const deltaX = Number(second.clientX) - Number(first.clientX);
  const deltaY = Number(second.clientY) - Number(first.clientY);
  const distance = Math.hypot(deltaX, deltaY);

  if (!Number.isFinite(distance) || distance <= 0) {
    return null;
  }

  const clientX = (Number(first.clientX) + Number(second.clientX)) / 2;
  const clientY = (Number(first.clientY) + Number(second.clientY)) / 2;

  return Number.isFinite(clientX) && Number.isFinite(clientY)
    ? { distance, clientX, clientY }
    : null;
};

const resolveElementScale = (element) => {
  if (!element || typeof element.getBoundingClientRect !== 'function') {
    return { x: 1, y: 1 };
  }

  const rect = element.getBoundingClientRect();
  const layoutWidth = Number(element.offsetWidth) || Number(element.clientWidth);
  const layoutHeight = Number(element.offsetHeight) || Number(element.clientHeight);

  let scaleX = 1;
  let scaleY = 1;

  if (rect && Number.isFinite(rect.width) && rect.width > 0 && layoutWidth > 0) {
    scaleX = rect.width / layoutWidth;
  }

  if (rect && Number.isFinite(rect.height) && rect.height > 0 && layoutHeight > 0) {
    scaleY = rect.height / layoutHeight;
  }

  if (!Number.isFinite(scaleX) || scaleX <= 0) {
    scaleX = 1;
  }

  if (!Number.isFinite(scaleY) || scaleY <= 0) {
    scaleY = 1;
  }

  return { x: scaleX, y: scaleY };
};

const parsePositiveNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }

  if (typeof value === 'string') {
    const match = value.match(/([0-9]*\.?[0-9]+)/);
    if (match) {
      const parsed = Number.parseFloat(match[1]);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }
  }

  return null;
};

const parseGridDimensionsFromValue = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (Array.isArray(value)) {
    const [first, second] = value;
    const firstNumber = parsePositiveNumber(first);
    const secondNumber = parsePositiveNumber(second);

    if (firstNumber !== null || secondNumber !== null) {
      const resolvedFirst = firstNumber ?? secondNumber;
      const resolvedSecond = secondNumber ?? firstNumber;
      if (resolvedFirst !== null) {
        return {
          columns: Math.max(1, resolvedFirst),
          rows: Math.max(1, resolvedSecond ?? resolvedFirst),
        };
      }
    }

    return null;
  }

  const numericValue = parsePositiveNumber(value);
  if (numericValue !== null) {
    const safeValue = Math.max(1, numericValue);
    return { columns: safeValue, rows: safeValue };
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const pairMatch = trimmed.match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
    if (pairMatch) {
      const [, columnMatch, rowMatch] = pairMatch;
      const parsedColumns = Number.parseFloat(columnMatch);
      const parsedRows = Number.parseFloat(rowMatch);
      if (Number.isFinite(parsedColumns) && Number.isFinite(parsedRows)) {
        return {
          columns: Math.max(1, parsedColumns),
          rows: Math.max(1, parsedRows),
        };
      }
    }
  }

  return null;
};

const resolveGridDimensions = (map) => {
  if (!map || typeof map !== 'object') {
    return { columns: DEFAULT_GRID_DIMENSION, rows: DEFAULT_GRID_DIMENSION };
  }

  const assignDimension = (current, value) => {
    const parsed = parsePositiveNumber(value);
    if (parsed === null) {
      return current;
    }

    const safeValue = Math.max(1, parsed);
    return current ?? safeValue;
  };

  let columns = null;
  let rows = null;

  const gridLikeObjects = [map.grid, map.meta, map.metadata, map.settings, map.details];
  const directColumnCandidates = [
    map.gridColumns,
    map.gridWidth,
    map.columns,
    map.widthSquares,
    map.width,
  ];
  const directRowCandidates = [
    map.gridRows,
    map.gridHeight,
    map.rows,
    map.heightSquares,
    map.height,
  ];

  directColumnCandidates.forEach((candidate) => {
    columns = assignDimension(columns, candidate);
  });
  directRowCandidates.forEach((candidate) => {
    rows = assignDimension(rows, candidate);
  });

  gridLikeObjects.forEach((obj) => {
    if (!obj || typeof obj !== 'object') {
      return;
    }

    columns = assignDimension(columns, obj.columns ?? obj.cols ?? obj.width);
    rows = assignDimension(rows, obj.rows ?? obj.height);

    if (columns !== null && rows !== null) {
      return;
    }

    const dimensionsValue =
      obj.dimensions ?? obj.size ?? obj.gridSize ?? obj.gridDimensions ?? obj.shape;
    const parsedDimensions = parseGridDimensionsFromValue(dimensionsValue);
    if (parsedDimensions) {
      if (columns === null && parsedDimensions.columns) {
        columns = parsedDimensions.columns;
      }
      if (rows === null && parsedDimensions.rows) {
        rows = parsedDimensions.rows;
      }
    }
  });

  const fallbackDimensionStrings = [
    map.gridSize,
    map.gridDimensions,
    map.dimensions,
    map.size,
    map.mapSize,
  ];

  fallbackDimensionStrings.forEach((value) => {
    if (columns !== null && rows !== null) {
      return;
    }

    const parsed = parseGridDimensionsFromValue(value);
    if (!parsed) {
      return;
    }

    if (columns === null && parsed.columns) {
      columns = parsed.columns;
    }
    if (rows === null && parsed.rows) {
      rows = parsed.rows;
    }
  });

  const safeColumns = columns ?? DEFAULT_GRID_DIMENSION;
  const safeRows = rows ?? safeColumns;

  return {
    columns: Math.max(1, safeColumns),
    rows: Math.max(1, safeRows),
  };
};

const resolveSquareSizeFromMetadata = (map) => {
  if (!map || typeof map !== 'object') {
    return null;
  }

  const candidatePaths = [
    ['squareSize'],
    ['square_size'],
    ['squareSizePixels'],
    ['squareSizePx'],
    ['squarePixelSize'],
    ['square_pixels'],
    ['square_size_pixels'],
    ['squareDimension'],
    ['pixelsPerSquare'],
    ['pixelPerSquare'],
    ['gridSquareSize'],
    ['grid', 'squareSize'],
    ['grid', 'cellSize'],
    ['grid', 'pixelsPerSquare'],
    ['grid', 'squarePixels'],
    ['meta', 'squareSize'],
    ['meta', 'pixelsPerSquare'],
    ['metadata', 'squareSize'],
    ['metadata', 'pixelsPerSquare'],
    ['settings', 'squareSize'],
    ['settings', 'pixelsPerSquare'],
    ['details', 'squareSize'],
    ['details', 'pixelsPerSquare'],
  ];

  for (const path of candidatePaths) {
    let current = map;
    let found = true;

    for (const key of path) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key];
      } else {
        found = false;
        break;
      }
    }

    if (!found) {
      continue;
    }

    const parsed = parsePositiveNumber(current);
    if (parsed !== null) {
      return parsed;
    }
  }

  return null;
};

const resolveFigurineSizeKey = (value) => {
  if (typeof value !== 'string') {
    return 'medium';
  }

  const trimmed = value.trim().toLowerCase();
  if (!trimmed) {
    return 'medium';
  }

  if (FIGURINE_SIZE_MULTIPLIERS[trimmed]) {
    return trimmed;
  }

  const tokens = trimmed.split(/[^a-z]+/).filter(Boolean);
  const tokenMatch = tokens.find((token) => FIGURINE_SIZE_MULTIPLIERS[token]);
  if (tokenMatch) {
    return tokenMatch;
  }

  const prefixMatch = Object.keys(FIGURINE_SIZE_MULTIPLIERS).find((key) =>
    trimmed.startsWith(key)
  );
  if (prefixMatch) {
    return prefixMatch;
  }

  return 'medium';
};

const resolveFigurineSquaresFromImageSize = (metrics, metadataSquareSize) => {
  if (!metrics || typeof metrics !== 'object') {
    return null;
  }

  const { width, height } = metrics;
  const numericWidth = Number(width);
  const numericHeight = Number(height);

  if (!Number.isFinite(numericWidth) || !Number.isFinite(numericHeight)) {
    return null;
  }

  if (numericWidth <= 0 || numericHeight <= 0) {
    return null;
  }

  const maxDimension = Math.max(numericWidth, numericHeight);
  if (!Number.isFinite(maxDimension) || maxDimension <= 0) {
    return null;
  }

  if (maxDimension < FALLBACK_FIGURINE_PIXEL_SQUARE_SIZE) {
    return 1;
  }

  const squareSize = (() => {
    if (Number.isFinite(metadataSquareSize) && metadataSquareSize > 0) {
      if (metadataSquareSize >= FALLBACK_FIGURINE_PIXEL_SQUARE_SIZE) {
        return metadataSquareSize;
      }

      if (maxDimension >= FALLBACK_FIGURINE_PIXEL_SQUARE_SIZE) {
        return metadataSquareSize;
      }
    }

    return FALLBACK_FIGURINE_PIXEL_SQUARE_SIZE;
  })();

  if (!Number.isFinite(squareSize) || squareSize <= 0) {
    return null;
  }

  const rawSquares = maxDimension / squareSize;

  if (!Number.isFinite(rawSquares) || rawSquares <= 0) {
    return null;
  }

  const rounded = Math.round(rawSquares);
  return Math.max(1, Math.min(MAX_FIGURINE_GRID_SQUARES, rounded));
};

const ROTATION_STEP_DEGREES = 15;
// How far a turn button turns the chosen sticker, as a key press does.
const STICKER_TURN_STEP = 15;

const TWO_PI = Math.PI * 2;

const MAP_PAN_DRAG_THRESHOLD_PX = 5;
const MAP_PAN_DRAG_THRESHOLD_SQUARED = MAP_PAN_DRAG_THRESHOLD_PX ** 2;

const resolvePointerValue = (primary, fallback) => {
  const primaryNumber = Number(primary);
  if (Number.isFinite(primaryNumber)) {
    return primaryNumber;
  }

  const fallbackNumber = Number(fallback);
  if (Number.isFinite(fallbackNumber)) {
    return fallbackNumber;
  }

  return null;
};

const resolvePointerCoordinates = (event) => ({
  x: resolvePointerValue(event?.clientX, event?.pageX),
  y: resolvePointerValue(event?.clientY, event?.pageY),
});

const normalizeDegrees = (value) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return 0;
  }

  const normalized = parsed % 360;
  return normalized < 0 ? normalized + 360 : normalized;
};

const radiansToDegrees = (radians) => (Number(radians) * 180) / Math.PI;
const degreesToRadians = (degrees) => (Number(degrees) * Math.PI) / 180;

const CampaignMapBoard = ({
  campaignId,
  combatState,
  fogView = 'player',
  map,
  tokens,
  onTokenDragStart,
  onTokenDrag,
  onTokenDragEnd,
  onTokenPositionChange,
  movementCells = [], movementPath, movementFootprintSize=1,
  onBackgroundClick,
  onTokenRemove,
  onTokenClick,
  onToggleCombat,
  placementControls,
  targeting,
  rangePreview,
  areaTargeting,
  persistentEffects,
  onAreaSelected,
  repositionToken,
  disabled,
  className,
  children,
  allowWheelZoom,
}) => {
  const [areaGeometry, setAreaGeometry] = useState(null);
  useEffect(() => { setAreaGeometry(null); }, [areaTargeting]);
  const pointerEventsSupported = usePointerEventsSupported();
  const safeMap = useMemo(() => map && typeof map === 'object' ? map : {}, [map]);
  const fogState = useMapFog(campaignId, safeMap, fogView === 'dm');
  const fog = { ...fogState, canEdit: fogView === 'dm' && fogState.canEdit };
  const customTokenScales = useCustomTokenScales(campaignId);
  const [fogPainting, setFogPainting] = useState(false);
  useEffect(()=>{if(repositionToken)setFogPainting(false);},[repositionToken]);
  // Fog brush: a square brush (1, 2, 3 or 5 squares across) that reveals or hides whole grid squares.
  const [fogBrushSize, setFogBrushSize] = useState(3);
  const [fogBrushMode, setFogBrushMode] = useState('reveal');
  useEffect(() => { setFogPainting(false); }, [campaignId, safeMap.mapId]);
  // The DM moves and turns the map's stickers during play, from the map controls.
  const stickerMoves = useMapStickerMoves(campaignId, safeMap);
  const canMoveStickers = fogView === 'dm' && Boolean(campaignId && safeMap.mapId) && (stickerMoves.layout?.stickers || []).length > 0;
  const [movingStickers, setMovingStickers] = useState(false);
  const [chosenStickerId, setChosenStickerId] = useState(null);
  useEffect(() => { setMovingStickers(false); setChosenStickerId(null); }, [campaignId, safeMap.mapId]);
  const editingStickers = movingStickers && canMoveStickers;
  const title = normalizeText(safeMap.title);
  const altText =
    normalizeText(safeMap.altText) ||
    title ||
    normalizeText(safeMap.prompt) ||
    'Campaign map image';
  const imageSrc = resolveMapImageSource(safeMap);

  const interactionDisabled = disabled || !imageSrc;

  const boardRef = useRef(null);
  const layerRef = useRef(null);
  const dragStateRef = useRef({ tokenId: null, pointerId: null });
  const [dragPositions, setDragPositions] = useState({});
  const [occupancyPreview, setOccupancyPreview] = useState(null);
  const [activeLabelTokenId, setActiveLabelTokenId] = useState(null);
  const [lastDraggedTokenId, setLastDraggedTokenId] = useState(null);
  const [hoveredTokenId, setHoveredTokenId] = useState(null);
  // The creature hovered while a range preview shows, and how the attack or spell would go against it.
  const [previewTargetId, setPreviewTargetId] = useState(null);
  useEffect(() => { if (!rangePreview) setPreviewTargetId(null); }, [rangePreview]);
  const previewOutcome = useMemo(() => (rangePreview && previewTargetId && previewTargetId !== rangePreview.sourceId
    ? rangePreview.evaluate?.(previewTargetId) || null : null), [rangePreview, previewTargetId]);
  const [rotationOverrides, setRotationOverrides] = useState({});
  const rotationOverridesRef = useRef({});
  const [draggingRotationTokenId, setDraggingRotationTokenId] = useState(null);
  const [mapPanOffset, setMapPanOffset] = useState({ x: 0, y: 0 });
  const [mapImageMetrics, setMapImageMetrics] = useState(null);
  const mapPanOffsetRef = useRef(mapPanOffset);
  const mapPanStateRef = useRef(null);
  const [isMapPanning, setIsMapPanning] = useState(false);
  const tokenPositionsRef = useRef([]);
  const [layerNode, setLayerNode] = useState(null);
  const [figurineImageMetrics, setFigurineImageMetrics] = useState({});
  const [mapZoom, setMapZoom] = useState(MAP_ZOOM_DEFAULT);
  const mapZoomRef = useRef(MAP_ZOOM_DEFAULT);
  const mapZoomRafRef = useRef(null);
  const mapZoomTargetRef = useRef(MAP_ZOOM_DEFAULT);
  const mapZoomAnimationStateRef = useRef({ lastTimestamp: null });
  const touchZoomStateRef = useRef(null);
  const touchPanAfterPinchRef = useRef(null);
  const resolvedMapZoom = useMemo(() => clampMapZoom(mapZoom), [mapZoom]);
  const rotationDragStateRef = useRef(null);
  const rotationMoveHandlerRef = useRef(null);
  const rotationUpHandlerRef = useRef(null);
  const rotationCancelHandlerRef = useRef(null);
  const handleLayerRef = useCallback((node) => {
    layerRef.current = node;
    setLayerNode(node);
  }, []);

  const handleFigurineImageLoad = useCallback((metricKey, target) => {
    if (!metricKey || !target) {
      return;
    }

    const { naturalWidth, naturalHeight } = target;

    if (!Number.isFinite(naturalWidth) || !Number.isFinite(naturalHeight)) {
      return;
    }

    const imageUrl=target.currentSrc || target.src;
    if(imageUrl)loadArtworkBounds(imageUrl).then(artwork=>{
      if(!artwork)return;
      setFigurineImageMetrics(prev=>({...prev,[metricKey]:{...prev[metricKey],artwork}}));
    });
    setFigurineImageMetrics((prev) => {
      const nextWidth = Math.round(naturalWidth);
      const nextHeight = Math.round(naturalHeight);
      const existing = prev[metricKey];

      if (existing && existing.width === nextWidth && existing.height === nextHeight) {
        return prev;
      }

      return {
        ...prev,
        [metricKey]: {
          ...existing,
          width: nextWidth,
          height: nextHeight,
        },
      };
    });
  }, []);

  const { columns: gridColumns, rows: gridRows } = useMemo(
    () => resolveGridDimensions(map),
    [map]
  );
  const stageAspectRatioData = useMemo(() => {
    if (Number.isFinite(gridColumns) && Number.isFinite(gridRows)) {
      const safeColumns = Math.max(1, Number(gridColumns));
      const safeRows = Math.max(1, Number(gridRows));

      if (safeColumns > 0 && safeRows > 0) {
        return {
          cssRatio: `${safeColumns} / ${safeRows}`,
          numericRatio: safeColumns / safeRows,
        };
      }
    }

    const metricsWidth = Number(mapImageMetrics?.width);
    const metricsHeight = Number(mapImageMetrics?.height);

    if (Number.isFinite(metricsWidth) && Number.isFinite(metricsHeight)) {
      if (metricsWidth > 0 && metricsHeight > 0) {
        return {
          cssRatio: `${metricsWidth} / ${metricsHeight}`,
          numericRatio: metricsWidth / metricsHeight,
        };
      }
    }

    return null;
  }, [gridColumns, gridRows, mapImageMetrics?.height, mapImageMetrics?.width]);

  const stageAspectRatio = stageAspectRatioData?.cssRatio ?? null;
  const stageAspectRatioNumber = stageAspectRatioData?.numericRatio ?? null;
  const metadataSquareSize = useMemo(() => resolveSquareSizeFromMetadata(map), [map]);

  useEffect(() => {
    mapPanOffsetRef.current = mapPanOffset;
  }, [mapPanOffset]);

  useEffect(() => {
    mapZoomRef.current = mapZoom;
    if (mapZoomRafRef.current === null) {
      mapZoomTargetRef.current = mapZoom;
    }
  }, [mapZoom]);

  useEffect(() => {
    setMapImageMetrics(null);
  }, [imageSrc]);

  useEffect(() => {
    return () => {
      const cancelFrame =
        typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function'
          ? window.cancelAnimationFrame
          : null;

      if (cancelFrame && mapZoomRafRef.current !== null) {
        cancelFrame(mapZoomRafRef.current);
        mapZoomRafRef.current = null;
      }
      mapZoomAnimationStateRef.current.lastTimestamp = null;
    };
  }, []);

  const handleMapImageLoad = useCallback((event) => {
    const target = event?.target;
    if (!target) {
      return;
    }

    const nextWidth = Number(target.naturalWidth);
    const nextHeight = Number(target.naturalHeight);

    if (!Number.isFinite(nextWidth) || !Number.isFinite(nextHeight)) {
      return;
    }

    if (nextWidth <= 0 || nextHeight <= 0) {
      return;
    }

    setMapImageMetrics((prev) => {
      if (prev && prev.width === nextWidth && prev.height === nextHeight) {
        return prev;
      }

      return { width: nextWidth, height: nextHeight };
    });
  }, []);

  const applyMapZoomAndPan = useCallback((nextZoom, anchor = null) => {
    const clampedNextZoom = clampMapZoom(nextZoom);
    const previousZoom = clampMapZoom(mapZoomRef.current);

    if (anchor && clampedNextZoom !== previousZoom) {
      const nextPan = calculateCursorAnchoredPan({
        anchor,
        pan: mapPanOffsetRef.current,
        oldScale: previousZoom,
        newScale: clampedNextZoom,
      });
      mapPanOffsetRef.current = nextPan;
      setMapPanOffset(nextPan);
    }

    mapZoomRef.current = clampedNextZoom;
    setMapZoom(clampedNextZoom);
  }, []);

  const handleMapZoomAnimationFrame = useCallback(
    (timestamp) => {
      mapZoomRafRef.current = null;

      const state = mapZoomAnimationStateRef.current;
      const targetZoom = clampMapZoom(mapZoomTargetRef.current);

      if (!Number.isFinite(targetZoom)) {
        state.lastTimestamp = null;
        return;
      }

      const lastTimestamp = state.lastTimestamp ?? timestamp;
      const deltaMilliseconds = Math.max(0, timestamp - lastTimestamp);
      state.lastTimestamp = timestamp;

      const previous = clampMapZoom(mapZoomRef.current);
      const difference = targetZoom - previous;
      const differenceMagnitude = Math.abs(difference);

      if (differenceMagnitude <= MAP_ZOOM_EPSILON) {
        state.lastTimestamp = null;
        applyMapZoomAndPan(targetZoom, state.anchor);
        return;
      }

      const clampedDeltaMs = Math.min(deltaMilliseconds, 200);
      const smoothingFactor = 1 - Math.exp(-clampedDeltaMs / MAP_ZOOM_ANIMATION_TIME_CONSTANT_MS);
      const easedNextZoom = clampMapZoom(previous + difference * smoothingFactor);

      if (Math.abs(targetZoom - easedNextZoom) <= MAP_ZOOM_EPSILON) {
        state.lastTimestamp = null;
        applyMapZoomAndPan(targetZoom, state.anchor);
        return;
      }

      applyMapZoomAndPan(easedNextZoom, state.anchor);

      const requestFrame =
        typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
          ? window.requestAnimationFrame
          : null;

      if (requestFrame) {
        mapZoomRafRef.current = requestFrame(handleMapZoomAnimationFrame);
      } else {
        mapZoomAnimationStateRef.current.lastTimestamp = null;
        applyMapZoomAndPan(targetZoom, state.anchor);
      }
    },
    [applyMapZoomAndPan]
  );

  const scheduleMapZoomUpdate = useCallback(
    (nextZoom, anchor) => {
      if (!Number.isFinite(nextZoom)) {
        return;
      }

      const clampedNextZoom = clampMapZoom(nextZoom);
      mapZoomTargetRef.current = clampedNextZoom;
      mapZoomAnimationStateRef.current.anchor = anchor;

      const requestFrame =
        typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
          ? window.requestAnimationFrame
          : null;

      if (!requestFrame) {
        applyMapZoomAndPan(clampedNextZoom, anchor);
        return;
      }

      if (mapZoomRafRef.current !== null) {
        return;
      }

      mapZoomAnimationStateRef.current.lastTimestamp = null;
      mapZoomRafRef.current = requestFrame(handleMapZoomAnimationFrame);
    },
    [applyMapZoomAndPan, handleMapZoomAnimationFrame]
  );

  const applyMapZoomImmediately = useCallback((nextZoom) => {
    if (!Number.isFinite(nextZoom)) {
      return;
    }

    const clampedNextZoom = clampMapZoom(nextZoom);
    const cancelFrame =
      typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function'
        ? window.cancelAnimationFrame
        : null;

    if (cancelFrame && mapZoomRafRef.current !== null) {
      cancelFrame(mapZoomRafRef.current);
    }

    mapZoomRafRef.current = null;
    mapZoomAnimationStateRef.current.lastTimestamp = null;
    mapZoomTargetRef.current = clampedNextZoom;
    applyMapZoomAndPan(clampedNextZoom);
  }, [applyMapZoomAndPan]);

  const panStyle = useMemo(() => {
    const style = {
      '--campaign-map-pan-x': `${mapPanOffset.x}px`,
      '--campaign-map-pan-y': `${mapPanOffset.y}px`,
    };

    if (stageAspectRatio) {
      style['--campaign-map-stage-aspect-ratio'] = stageAspectRatio;
    }

    if (Number.isFinite(stageAspectRatioNumber) && stageAspectRatioNumber > 0) {
      style['--campaign-map-stage-ratio-number'] = stageAspectRatioNumber;
    }

    return style;
  }, [mapPanOffset.x, mapPanOffset.y, stageAspectRatio, stageAspectRatioNumber]);

  useEffect(() => {
    mapPanStateRef.current = null;
    setIsMapPanning(false);
    setMapPanOffset((prev) => {
      if (prev.x === 0 && prev.y === 0) {
        return prev;
      }
      return { x: 0, y: 0 };
    });
    mapPanOffsetRef.current = { x: 0, y: 0 };
    const cancelFrame =
      typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function'
        ? window.cancelAnimationFrame
        : null;
    if (cancelFrame && mapZoomRafRef.current !== null) {
      cancelFrame(mapZoomRafRef.current);
      mapZoomRafRef.current = null;
    }
    mapZoomAnimationStateRef.current.lastTimestamp = null;
    mapZoomTargetRef.current = MAP_ZOOM_DEFAULT;
    mapZoomRef.current = MAP_ZOOM_DEFAULT;
    setMapZoom(MAP_ZOOM_DEFAULT);
  }, [imageSrc]);

  useEffect(() => {
    if (!allowWheelZoom) {
      const cancelFrame =
        typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function'
          ? window.cancelAnimationFrame
          : null;
      if (cancelFrame && mapZoomRafRef.current !== null) {
        cancelFrame(mapZoomRafRef.current);
        mapZoomRafRef.current = null;
      }
      mapZoomAnimationStateRef.current.lastTimestamp = null;
      mapZoomTargetRef.current = MAP_ZOOM_DEFAULT;
      mapZoomRef.current = MAP_ZOOM_DEFAULT;
      setMapZoom(MAP_ZOOM_DEFAULT);
    }
  }, [allowWheelZoom]);

  const boardStyle = useMemo(() => {
    const zoomValue = Number.isFinite(resolvedMapZoom) ? resolvedMapZoom : MAP_ZOOM_DEFAULT;

    if (!allowWheelZoom) {
      return {};
    }

    return {
      '--campaign-map-zoom': `${zoomValue}`,
      '--map-modal-background-scale': `${zoomValue}`,
      '--campaign-map-grid-extent': `${zoomValue * 100}%`,
      '--campaign-map-grid-origin': `${(1 - zoomValue) * 50}%`,
    };
  }, [allowWheelZoom, resolvedMapZoom]);

  const handleWheel = useCallback(
    (event) => {
      if (!allowWheelZoom) {
        return;
      }

      const wheelEvent = event?.nativeEvent ?? event;
      if (!wheelEvent || typeof wheelEvent.deltaY !== 'number' || wheelEvent.deltaY === 0) {
        return;
      }

      if (typeof event?.preventDefault === 'function') {
        event.preventDefault();
      }

      if (typeof event?.stopPropagation === 'function') {
        event.stopPropagation();
      }

      const { deltaY, ctrlKey, deltaMode } = wheelEvent;

      const deltaPixels = (() => {
        if (typeof deltaMode !== 'number') {
          return deltaY;
        }

        if (deltaMode === 1) {
          return deltaY * 16;
        }

        if (deltaMode === 2) {
          return deltaY * 800;
        }

        return deltaY;
      })();

      if (!Number.isFinite(deltaPixels) || deltaPixels === 0) {
        return;
      }

      const normalizedDelta = Math.max(-1, Math.min(1, deltaPixels / 120));
      const zoomStrength = ctrlKey ? 0.12 : 0.2;
      const zoomMultiplier = 1 - normalizedDelta * zoomStrength;

      if (!Number.isFinite(zoomMultiplier) || zoomMultiplier <= 0) {
        return;
      }

      const safePrevious = clampMapZoom(mapZoomTargetRef.current);
      const nextZoom = clampMapZoom(safePrevious * zoomMultiplier);
      const viewport = layerRef.current?.parentElement;
      const rect = viewport?.getBoundingClientRect?.();
      const clientX = Number(wheelEvent.clientX);
      const clientY = Number(wheelEvent.clientY);

      let anchor = null;
      if (
        rect &&
        Number.isFinite(clientX) &&
        Number.isFinite(clientY) &&
        Number.isFinite(rect.left) &&
        Number.isFinite(rect.top) &&
        Number.isFinite(rect.width) &&
        Number.isFinite(rect.height)
      ) {
        const { x: outerScaleX, y: outerScaleY } = resolveElementScale(viewport);
        anchor = {
          x: (clientX - (rect.left + rect.width / 2)) / outerScaleX,
          y: (clientY - (rect.top + rect.height / 2)) / outerScaleY,
        };
      }

      scheduleMapZoomUpdate(nextZoom, anchor);
    },
    [allowWheelZoom, scheduleMapZoomUpdate]
  );


  const handleTouchStart = useCallback(
    (event) => {
      if (!allowWheelZoom) {
        return;
      }

      const touches = event?.touches;
      const geometry = getTouchGeometry(touches);
      if (!geometry) {
        touchZoomStateRef.current = null;
        return;
      }
      touchPanAfterPinchRef.current = null;

      event.preventDefault();
      event.stopPropagation();
      mapPanStateRef.current = null;
      setIsMapPanning(false);

      const viewport = layerRef.current?.parentElement;
      const rect = viewport?.getBoundingClientRect?.();
      if (!rect) {
        touchZoomStateRef.current = null;
        return;
      }
      const { x: outerScaleX, y: outerScaleY } = resolveElementScale(viewport);
      const anchor = {
        x: (geometry.clientX - (rect.left + rect.width / 2)) / outerScaleX,
        y: (geometry.clientY - (rect.top + rect.height / 2)) / outerScaleY,
      };
      const zoom = clampMapZoom(mapZoomRef.current);
      touchZoomStateRef.current = {
        distance: geometry.distance,
        zoom,
        anchor,
        pan: { ...mapPanOffsetRef.current },
      };
    },
    [allowWheelZoom]
  );

  const handleTouchMove = useCallback(
    (event) => {
      if (!allowWheelZoom) {
        return;
      }

      const geometry = getTouchGeometry(event?.touches);
      const state = touchZoomStateRef.current;
      if (!geometry) {
        const touch = event?.touches?.length === 1 ? event.touches[0] : null;
        const panState = touchPanAfterPinchRef.current;
        if (!touch || !panState) return;
        event.preventDefault();
        event.stopPropagation();
        const nextPan = {
          x: panState.pan.x + (Number(touch.clientX) - panState.clientX) / panState.scaleX,
          y: panState.pan.y + (Number(touch.clientY) - panState.clientY) / panState.scaleY,
        };
        mapPanOffsetRef.current = nextPan;
        setMapPanOffset(nextPan);
        touchPanAfterPinchRef.current = {
          ...panState,
          clientX: Number(touch.clientX),
          clientY: Number(touch.clientY),
          pan: nextPan,
        };
        setIsMapPanning(true);
        return;
      }
      if (!state || !Number.isFinite(state.distance) || state.distance <= 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      const viewport = layerRef.current?.parentElement;
      const rect = viewport?.getBoundingClientRect?.();
      if (!rect) return;
      const { x: outerScaleX, y: outerScaleY } = resolveElementScale(viewport);
      const anchor = {
        x: (geometry.clientX - (rect.left + rect.width / 2)) / outerScaleX,
        y: (geometry.clientY - (rect.top + rect.height / 2)) / outerScaleY,
      };
      const nextZoom = clampMapZoom(state.zoom * (geometry.distance / state.distance));
      const nextPan = calculateAnchoredPan({
        anchor,
        previousAnchor: state.anchor,
        pan: state.pan,
        oldScale: state.zoom,
        newScale: nextZoom,
      });

      mapPanOffsetRef.current = nextPan;
      setMapPanOffset(nextPan);
      applyMapZoomImmediately(nextZoom);

      // Rebase every frame, including while clamped, so reversing direction at
      // a zoom limit responds immediately instead of traversing hidden distance.
      touchZoomStateRef.current = {
        distance: geometry.distance,
        zoom: nextZoom,
        anchor,
        pan: nextPan,
      };
    },
    [allowWheelZoom, applyMapZoomImmediately]
  );

  const handleTouchEnd = useCallback((event) => {
    touchZoomStateRef.current = null;
    const remainingTouch = event?.touches?.length === 1 ? event.touches[0] : null;
    if (!remainingTouch) {
      mapPanStateRef.current = null;
      touchPanAfterPinchRef.current = null;
      setIsMapPanning(false);
      return;
    }

    // The remaining touch has no new start event after a pinch. Seed a
    // one-finger continuation at its current position to avoid a jump.
    const viewport = layerRef.current?.parentElement;
    const outerScale = resolveElementScale(viewport);
    mapPanStateRef.current = null;
    touchPanAfterPinchRef.current = {
      clientX: Number(remainingTouch.clientX),
      clientY: Number(remainingTouch.clientY),
      pan: { ...mapPanOffsetRef.current },
      scaleX: outerScale.x,
      scaleY: outerScale.y,
    };
  }, []);

  useEffect(() => {
    const boardElement = boardRef.current;

    if (!boardElement || !allowWheelZoom) {
      return () => {};
    }

    const preventNativePinchZoom = (event) => {
      if (event?.touches && event.touches.length >= 2) {
        event.preventDefault();
      }
    };

    const handleGestureStart = (event) => {
      if (typeof event?.preventDefault === 'function') {
        event.preventDefault();
      }
    };

    const handleGestureChange = (event) => {
      if (typeof event?.preventDefault === 'function') {
        event.preventDefault();
      }

      // Touch events above own the transform. WebKit gesture events are only
      // cancelled here so the browser cannot also zoom the page.
    };

    boardElement.addEventListener('touchmove', preventNativePinchZoom, { passive: false });
    boardElement.addEventListener('gesturestart', handleGestureStart, { passive: false });
    boardElement.addEventListener('gesturechange', handleGestureChange, { passive: false });

    return () => {
      boardElement.removeEventListener('touchmove', preventNativePinchZoom);
      boardElement.removeEventListener('gesturestart', handleGestureStart);
      boardElement.removeEventListener('gesturechange', handleGestureChange);
    };
  }, [allowWheelZoom]);

  useEffect(() => {
    const boardElement = boardRef.current;

    if (!boardElement) {
      return () => {};
    }

    const safeColumns = Math.max(1, gridColumns || DEFAULT_GRID_DIMENSION);
    const safeRows = Math.max(1, gridRows || safeColumns);
    const columnWidthPercent = `${100 / safeColumns}%`;
    const rowHeightPercent = `${100 / safeRows}%`;

    boardElement.style.setProperty('--campaign-map-grid-columns', `${safeColumns}`);
    boardElement.style.setProperty('--campaign-map-grid-rows', `${safeRows}`);
    boardElement.style.setProperty('--campaign-map-grid-cell-width', columnWidthPercent);
    boardElement.style.setProperty('--campaign-map-grid-cell-height', rowHeightPercent);

    const cleanupGridVariables = () => {
      boardElement.style.removeProperty('--campaign-map-grid-columns');
      boardElement.style.removeProperty('--campaign-map-grid-rows');
      boardElement.style.removeProperty('--campaign-map-grid-cell-width');
      boardElement.style.removeProperty('--campaign-map-grid-cell-height');
    };

    const layerElement = layerNode;

    if (!layerElement) {
      boardElement.style.removeProperty('--campaign-map-square-size');
      return () => {
        cleanupGridVariables();
      };
    }

    const updateSquareSize = () => {
      const rect = layerElement.getBoundingClientRect();
      const widthPerColumn = layerElement.clientWidth ? layerElement.clientWidth / safeColumns : rect.width ? rect.width / clampMapZoom(mapZoomRef.current) / safeColumns : metadataSquareSize;
      const heightPerRow = layerElement.clientHeight ? layerElement.clientHeight / safeRows : rect.height ? rect.height / clampMapZoom(mapZoomRef.current) / safeRows : metadataSquareSize;
      const candidates = [widthPerColumn, heightPerRow].filter(
        (value) => Number.isFinite(value) && value > 0
      );

      if (candidates.length === 0) {
        boardElement.style.removeProperty('--campaign-map-square-size');
        return;
      }

      const resolved = Math.min(...candidates);
      boardElement.style.setProperty('--campaign-map-square-size', `${resolved}px`);
    };

    updateSquareSize();

    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(() => {
        updateSquareSize();
      });
      observer.observe(layerElement);

      return () => {
        observer.disconnect();
        boardElement.style.removeProperty('--campaign-map-square-size');
        cleanupGridVariables();
      };
    }

    const handleResize = () => {
      updateSquareSize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      boardElement.style.removeProperty('--campaign-map-square-size');
      cleanupGridVariables();
    };
  }, [gridColumns, gridRows, layerNode, metadataSquareSize]);

  const tokenPositions = useMemo(() => {
    if (!Array.isArray(tokens)) {
      return [];
    }

    return tokens
      .filter((token) => token && typeof token === 'object' && token.characterId)
      .map((token) => {
        const { characterId } = token;
        const activePosition =
          dragPositions[characterId] || {
            x: clamp01(token.x) ?? 0,
            y: clamp01(token.y) ?? 0,
          };
        return {
          ...token,
          position: activePosition,
        };
      });
  }, [tokens, dragPositions]);

  const areaMap = useMemo(() => ({ ...safeMap, gridColumns, gridRows }), [safeMap, gridColumns, gridRows]);
  const visibleTokenPositions = useMemo(() => !fog.active || fog.canEdit ? tokenPositions : tokenPositions.filter(token => tokenRevealed(fog.mask, token, areaMap)), [tokenPositions, fog.active, fog.canEdit, fog.mask, areaMap]);
  // Fog conceals presentation, not creatures from an area's damage resolution.
  const areaTokens = areaTargeting?.areaCombatantIds ? tokenPositions.filter((token) => areaTargeting.areaCombatantIds.includes(token.characterId)) : tokenPositions;
  const sourceAreaToken = tokenPositions.find((token) => token.characterId === areaTargeting?.sourceCombatantId);
  const previewArea = areaGeometry;
  const areaStops = areaTargeting ? mapAreaBarriers(persistentEffects, areaMap) : null;
  const affectedAreaIds = previewArea ? areaTargetIds({ geometry: previewArea, tokens: areaTokens, map: areaMap, barriers: areaStops,
    sourceCombatantId: areaTargeting?.sourceCombatantId, allowZeroHp: areaTargeting?.actionType === 'healing' }) : [];

  useEffect(() => {
    tokenPositionsRef.current = tokenPositions;
  }, [tokenPositions]);

  useEffect(() => {
    rotationOverridesRef.current = rotationOverrides;
  }, [rotationOverrides]);


  useEffect(() => {
    const activeIds = new Set(tokenPositions.map((token) => token?.characterId));

    setRotationOverrides((prev) => {
      const next = {};
      activeIds.forEach((id) => {
        if (Object.prototype.hasOwnProperty.call(prev, id)) {
          next[id] = prev[id];
        }
      });

      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      if (
        prevKeys.length === nextKeys.length &&
        prevKeys.every((key) => next[key] === prev[key])
      ) {
        return prev;
      }

      return next;
    });

    setLastDraggedTokenId((prev) => (prev && !activeIds.has(prev) ? null : prev));
    setHoveredTokenId((prev) => (prev && !activeIds.has(prev) ? null : prev));
  }, [tokenPositions]);

  const resetDragState = useCallback(() => {
    dragStateRef.current = { tokenId: null, pointerId: null };
    setOccupancyPreview(null);
    setDragPositions((prev) => {
      if (Object.keys(prev).length === 0) {
        return prev;
      }
      return {};
    });
  }, []);

  const getNormalizedCoordinates = useCallback((clientX, clientY) => {
    const layer = layerRef.current;
    if (!layer) {
      return null;
    }

    const rect = layer.getBoundingClientRect();
    if (!rect || rect.width <= 0 || rect.height <= 0) {
      return null;
    }

    const rawX = (clientX - rect.left) / rect.width;
    const rawY = (clientY - rect.top) / rect.height;

    const x = clamp01(rawX);
    const y = clamp01(rawY);

    if (x === null || y === null) {
      return null;
    }

    return { x, y };
  }, []);

  const getSnappedPlacement = useCallback((clientX, clientY, token, anchorOffset={x:0,y:0}) => {
    const normalized = getNormalizedCoordinates(clientX, clientY);
    if (!normalized) return null;
    const anchorCell = {
      x: Math.min(gridColumns - 1, Math.max(0, Math.floor(normalized.x * gridColumns)-anchorOffset.x)),
      y: Math.min(gridRows - 1, Math.max(0, Math.floor(normalized.y * gridRows)-anchorOffset.y)),
    };
    const size = getOccupiedSquareSize(token);
    const cells = getOccupiedGridCells({ anchorCell, size });
    return {
      anchorCell,
      cells,
      valid: cells.every((cell) => cell.x < gridColumns && cell.y < gridRows),
      // Stored normalized coordinates denote the center of the top-left anchor
      // cell; gridSpatial floors them back to this exact logical anchor.
      position: {
        x: (anchorCell.x + 0.5) / gridColumns,
        y: (anchorCell.y + 0.5) / gridRows,
      },
    };
  }, [getNormalizedCoordinates, gridColumns, gridRows]);

  const handlePointerDown = useCallback(
    (event, token) => {
      // Target selection takes precedence over every map editing interaction. The
      // subsequent click owns target selection, while pointer down must never
      // initialize a drag (including for DM-controlled movable tokens).
      if (targeting || repositionToken) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (interactionDisabled || !token || token.isMovable === false) {
        return;
      }

      if (typeof event?.button === 'number' && event.button !== 0) {
        return;
      }

      const { characterId } = token;
      if (typeof characterId !== 'string' || characterId.trim() === '') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if(token.movementLocked){setLastDraggedTokenId(characterId);return;}
      const pointer=getNormalizedCoordinates(event.clientX,event.clientY);
      const footprint=tokenFootprintLayout(token,token.position || token,gridColumns,gridRows);
      const anchorOffset=pointer?{x:Math.max(0,Math.min(footprint.size-1,Math.floor(pointer.x*gridColumns)-footprint.anchor.x)),y:Math.max(0,Math.min(footprint.size-1,Math.floor(pointer.y*gridRows)-footprint.anchor.y))}:{x:0,y:0};
      dragStateRef.current = { tokenId: characterId, pointerId: event.pointerId, anchorOffset };
      setDragPositions((prev) => ({
        ...prev,
        [characterId]: {
          x: clamp01(token.x) ?? 0,
          y: clamp01(token.y) ?? 0,
        },
      }));

      setLastDraggedTokenId(null);

      if (typeof onTokenDragStart === 'function') {
        onTokenDragStart({ token, characterId });
      }

      if (
        event.pointerId !== undefined &&
        event.currentTarget.setPointerCapture
      ) {
        try {
          event.currentTarget.setPointerCapture(event.pointerId);
        } catch (error) {
          // Ignore pointer capture errors (e.g., when unsupported)
        }
      }
    },
    [interactionDisabled, onTokenDragStart, targeting, repositionToken,getNormalizedCoordinates,gridColumns,gridRows]
  );

  const updateDragPosition = useCallback((tokenId, nextPosition) => {
    if (!tokenId || !nextPosition) {
      return;
    }
    setDragPositions((prev) => ({
      ...prev,
      [tokenId]: nextPosition,
    }));
  }, []);

  const handlePointerMove = useCallback(
    (event) => {
      const dragState = dragStateRef.current;
      if (!dragState || !dragState.tokenId) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const token = tokenPositionsRef.current.find(({ characterId }) => characterId === dragState.tokenId);
      const placement = getSnappedPlacement(event.clientX, event.clientY, token,dragState.anchorOffset);
      if (!placement) {
        return;
      }

      const coords = placement.position;

      updateDragPosition(dragState.tokenId, coords);
      setOccupancyPreview({ ...placement, characterId: dragState.tokenId });

      if (typeof onTokenDrag === 'function') {
        onTokenDrag({ characterId: dragState.tokenId, ...coords });
      }
    },
    [getSnappedPlacement, onTokenDrag, updateDragPosition]
  );

  const finalizeDrag = useCallback(
    (event, cancelled = false) => {
      const dragState = dragStateRef.current;
      if (!dragState || !dragState.tokenId) {
        return;
      }

      const { tokenId, pointerId } = dragState;
      if (
        pointerId !== undefined &&
        event.pointerId !== undefined &&
        event.pointerId !== pointerId
      ) {
        return;
      }

      const token = tokenPositionsRef.current.find(({ characterId }) => characterId === tokenId);
      const placement = getSnappedPlacement(event.clientX, event.clientY, token,dragState.anchorOffset);
      if (!placement) {
        resetDragState();
        return;
      }
      const coords = placement.position;

      updateDragPosition(tokenId, coords);

      if (typeof onTokenPositionChange === 'function' && !cancelled) {
        onTokenPositionChange({ characterId: tokenId, ...coords });
      }

      if (typeof onTokenDragEnd === 'function') {
        onTokenDragEnd({ characterId: tokenId, ...coords, cancelled });
      }

      resetDragState();
      setLastDraggedTokenId(cancelled ? null : tokenId);
    },
    [getSnappedPlacement, onTokenDragEnd, onTokenPositionChange, resetDragState, updateDragPosition]
  );

  useEffect(() => {
    const cancelOnEscape = (event) => {
      if (event.key !== 'Escape' || !dragStateRef.current?.tokenId) return;
      event.preventDefault();
      const tokenId = dragStateRef.current.tokenId;
      onTokenDragEnd?.({ characterId: tokenId, cancelled: true });
      resetDragState();
      setLastDraggedTokenId(null);
    };
    window.addEventListener('keydown', cancelOnEscape);
    return () => window.removeEventListener('keydown', cancelOnEscape);
  }, [onTokenDragEnd, resetDragState]);

  const handlePointerUp = useCallback(
    (event) => {
      const dragState = dragStateRef.current;
      if (!dragState || !dragState.tokenId) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (dragState.tokenId) {
        setLastDraggedTokenId(dragState.tokenId);
      }
      finalizeDrag(event, false);
    },
    [finalizeDrag]
  );

  const handlePointerCancel = useCallback(
    (event) => {
      const dragState = dragStateRef.current;
      if (!dragState || !dragState.tokenId) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      finalizeDrag(event, true);
    },
    [finalizeDrag]
  );

  const combatShortcuts = useMapCombatShortcuts({
    enabled: fogView === 'dm' && typeof onToggleCombat === 'function' && !interactionDisabled && !fogPainting && !targeting && !areaTargeting && !repositionToken,
    onToggle: onToggleCombat, onDown: handlePointerDown, onMove: handlePointerMove,
    onUp: handlePointerUp, onCancel: handlePointerCancel,
    onTap: token => { setLastDraggedTokenId(token.characterId); onTokenClick?.(token.characterId, token); },
  });

  const handleLayerPointerDown = useCallback(
    (event) => {
      setActiveLabelTokenId(null);
      setLastDraggedTokenId(null);
      setHoveredTokenId(null);
      if (targeting) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (interactionDisabled) {
        return;
      }

      if (event.target !== event.currentTarget) {
        return;
      }

      if (typeof event?.button === 'number' && event.button !== 0) {
        return;
      }

      const { x: startX, y: startY } = resolvePointerCoordinates(event);
      const resolvedStartX = Number.isFinite(startX) ? startX : 0;
      const resolvedStartY = Number.isFinite(startY) ? startY : 0;
      const initialCoords =
        Number.isFinite(startX) && Number.isFinite(startY)
          ? getNormalizedCoordinates(startX, startY)
          : null;

      const { x: scaleX, y: scaleY } = resolveElementScale(event.currentTarget);

      mapPanStateRef.current = {
        pointerId: event.pointerId,
        startClientX: resolvedStartX,
        startClientY: resolvedStartY,
        originX: mapPanOffsetRef.current.x,
        originY: mapPanOffsetRef.current.y,
        initialCoords: initialCoords || null,
        hasDragged: false,
        allowBackgroundClick: typeof onBackgroundClick === 'function',
        scaleX,
        scaleY,
        zoom: clampMapZoom(mapZoomTargetRef.current),
      };

      if (event.currentTarget.setPointerCapture) {
        try {
          event.currentTarget.setPointerCapture(event.pointerId);
        } catch (error) {
          // Ignore pointer capture errors (e.g., when unsupported)
        }
      }

      event.preventDefault();
      event.stopPropagation();
    },
    [getNormalizedCoordinates, interactionDisabled, onBackgroundClick, targeting]
  );

  const handleLayerPointerMove = useCallback((event) => {
    const panState = mapPanStateRef.current;
    if (!panState) {
      if (repositionToken) {
        const placement = getSnappedPlacement(event.clientX, event.clientY, repositionToken);
        setOccupancyPreview(placement ? { ...placement, characterId: repositionToken.characterId } : null);
      }
      return;
    }

    const { x: currentX, y: currentY } = resolvePointerCoordinates(event);
    if (!Number.isFinite(currentX) || !Number.isFinite(currentY)) {
      return;
    }

    if (
      panState.pointerId !== undefined &&
      event.pointerId !== undefined &&
      panState.pointerId !== event.pointerId
    ) {
      return;
    }

    const rawDeltaX = currentX - panState.startClientX;
    const rawDeltaY = currentY - panState.startClientY;

    if (!panState.hasDragged) {
      const distanceSquared = rawDeltaX * rawDeltaX + rawDeltaY * rawDeltaY;
      if (distanceSquared < MAP_PAN_DRAG_THRESHOLD_SQUARED) {
        return;
      }

      panState.hasDragged = true;
      setIsMapPanning(true);
    }

    event.preventDefault();
    event.stopPropagation();

    const panZoom = Number.isFinite(panState.zoom) && panState.zoom > 0 ? panState.zoom : 1;
    const scaleX =
      panState && Number.isFinite(panState.scaleX) && panState.scaleX > 0
        ? Math.max(0.001, panState.scaleX / panZoom)
        : 1;
    const scaleY =
      panState && Number.isFinite(panState.scaleY) && panState.scaleY > 0
        ? Math.max(0.001, panState.scaleY / panZoom)
        : 1;

    const deltaX = rawDeltaX / scaleX;
    const deltaY = rawDeltaY / scaleY;

    const nextX = panState.originX + deltaX;
    const nextY = panState.originY + deltaY;

    if (
      nextX === mapPanOffsetRef.current.x &&
      nextY === mapPanOffsetRef.current.y
    ) {
      return;
    }

    mapPanOffsetRef.current = { x: nextX, y: nextY };
    setMapPanOffset({ x: nextX, y: nextY });
  }, [getSnappedPlacement, repositionToken]);

  useEffect(() => {
    if (!repositionToken && !dragStateRef.current?.tokenId) setOccupancyPreview(null);
  }, [repositionToken]);

  const handleLayerPointerUp = useCallback(
    (event) => {
      const panState = mapPanStateRef.current;
      if (!panState) {
        return;
      }

      if (
        panState.pointerId !== undefined &&
        event.pointerId !== undefined &&
        panState.pointerId !== event.pointerId
      ) {
        return;
      }

      if (
        event.pointerId !== undefined &&
        event.currentTarget.releasePointerCapture
      ) {
        try {
          event.currentTarget.releasePointerCapture(event.pointerId);
        } catch (error) {
          // Ignore release errors – capturing might not be active or supported
        }
      }

      mapPanStateRef.current = null;
      mapPanOffsetRef.current = mapPanOffset;
      setIsMapPanning(false);

      if (panState.hasDragged) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (!panState.allowBackgroundClick) {
        return;
      }

      const { x: upX, y: upY } = resolvePointerCoordinates(event);
      const resolvedClientX = Number.isFinite(upX) ? upX : panState.startClientX;
      const resolvedClientY = Number.isFinite(upY) ? upY : panState.startClientY;
      const placement = repositionToken
        ? getSnappedPlacement(resolvedClientX, resolvedClientY, repositionToken)
        : null;
      if (repositionToken && !placement?.valid) return;
      const coords = placement?.position ||
        getNormalizedCoordinates(resolvedClientX, resolvedClientY) || panState.initialCoords;
      if (!coords) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (fog.active && !fog.canEdit && !revealed(fog.mask, coords.x, coords.y)) return;
      onBackgroundClick(coords);
    },
    [getNormalizedCoordinates, getSnappedPlacement, mapPanOffset, onBackgroundClick, repositionToken, fog.active, fog.canEdit, fog.mask]
  );

  const handleLayerPointerCancel = useCallback(
    (event) => {
      const panState = mapPanStateRef.current;
      if (!panState) {
        return;
      }

      if (
        panState.pointerId !== undefined &&
        event.pointerId !== undefined &&
        panState.pointerId !== event.pointerId
      ) {
        return;
      }

      if (
        event.pointerId !== undefined &&
        event.currentTarget.releasePointerCapture
      ) {
        try {
          event.currentTarget.releasePointerCapture(event.pointerId);
        } catch (error) {
          // Ignore release errors – capturing might not be active or supported
        }
      }

      mapPanStateRef.current = null;
      setIsMapPanning(false);
    },
    []
  );

  const getResolvedRotationForToken = useCallback(
    (token) => {
      if (!token || !token.characterId) {
        return 0;
      }

      const override = rotationOverrides[token.characterId];
      if (Number.isFinite(override)) {
        return normalizeDegrees(override);
      }

      const baseRotation = Number(token.rotation);
      return Number.isFinite(baseRotation) ? normalizeDegrees(baseRotation) : 0;
    },
    [rotationOverrides]
  );

  const stopRotationDrag = useCallback(() => {
    const activeDragState = rotationDragStateRef.current;
    const targets = [];
    if (typeof window !== 'undefined') {
      targets.push(window);
    }
    if (typeof document !== 'undefined') {
      targets.push(document);
    }

    if (
      activeDragState &&
      activeDragState.handleElement &&
      typeof activeDragState.handleElement.releasePointerCapture === 'function' &&
      activeDragState.pointerId !== undefined
    ) {
      try {
        activeDragState.handleElement.releasePointerCapture(activeDragState.pointerId);
      } catch (releaseError) {
        // Ignore release errors – capturing might not be active or supported
      }
    }

    if (rotationMoveHandlerRef.current) {
      targets.forEach((target) => {
        if (target && typeof target.removeEventListener === 'function') {
          if (typeof rotationMoveHandlerRef.current === 'function') {
            target.removeEventListener('pointermove', rotationMoveHandlerRef.current);
          } else {
            const handlers = rotationMoveHandlerRef.current;
            if (handlers?.mouse) {
              target.removeEventListener('mousemove', handlers.mouse);
            }
            if (handlers?.touch) {
              target.removeEventListener('touchmove', handlers.touch);
            }
          }
        }
      });
      rotationMoveHandlerRef.current = null;
    }

    if (rotationUpHandlerRef.current) {
      targets.forEach((target) => {
        if (target && typeof target.removeEventListener === 'function') {
          if (typeof rotationUpHandlerRef.current === 'function') {
            target.removeEventListener('pointerup', rotationUpHandlerRef.current);
          } else {
            const handlers = rotationUpHandlerRef.current;
            if (handlers?.mouse) {
              target.removeEventListener('mouseup', handlers.mouse);
            }
            if (handlers?.touch) {
              target.removeEventListener('touchend', handlers.touch);
            }
          }
        }
      });
      rotationUpHandlerRef.current = null;
    }

    if (rotationCancelHandlerRef.current) {
      targets.forEach((target) => {
        if (target && typeof target.removeEventListener === 'function') {
          if (typeof rotationCancelHandlerRef.current === 'function') {
            target.removeEventListener('pointercancel', rotationCancelHandlerRef.current);
          } else {
            const handlers = rotationCancelHandlerRef.current;
            if (handlers?.mouse) {
              target.removeEventListener('mouseleave', handlers.mouse);
              target.removeEventListener('mouseout', handlers.mouse);
            }
            if (handlers?.touch) {
              target.removeEventListener('touchcancel', handlers.touch);
            }
          }
        }
      });
      rotationCancelHandlerRef.current = null;
    }

    rotationDragStateRef.current = null;
    setDraggingRotationTokenId(null);
  }, [pointerEventsSupported, setDraggingRotationTokenId]);

  useEffect(() => () => stopRotationDrag(), [stopRotationDrag]);

  useEffect(() => {
    if (interactionDisabled) {
      stopRotationDrag();
    }
  }, [interactionDisabled, stopRotationDrag]);

  const previewTokenRotation = useCallback(
    (tokenId, rotation) => {
      if (!tokenId || !Number.isFinite(rotation)) {
        return;
      }

      const normalizedRotation = normalizeDegrees(rotation);

      setRotationOverrides((prev) => {
        if (prev[tokenId] === normalizedRotation) {
          return prev;
        }

        return {
          ...prev,
          [tokenId]: normalizedRotation,
        };
      });

      setLastDraggedTokenId(tokenId);
    },
    []
  );


  const applyTokenRotation = useCallback(
    (tokenId, rotation) => {
      if (!tokenId || !Number.isFinite(rotation)) {
        return;
      }

      previewTokenRotation(tokenId, rotation);

      const tokensList = tokenPositionsRef.current;
      const normalizedRotation = normalizeDegrees(rotation);

      if (typeof onTokenPositionChange === 'function' && Array.isArray(tokensList)) {
        const foundToken = tokensList.find((entry) => entry?.characterId === tokenId);
        if (foundToken) {
          const candidatePosition =
            foundToken.position && typeof foundToken.position === 'object'
              ? foundToken.position
              : null;
          const normalizedX = clamp01(candidatePosition?.x ?? foundToken.x);
          const normalizedY = clamp01(candidatePosition?.y ?? foundToken.y);

          if (normalizedX !== null && normalizedY !== null) {
            onTokenPositionChange({
              characterId: tokenId,
              x: normalizedX,
              y: normalizedY,
              rotation: normalizedRotation,
            });
          }
        }
      }
    },
    [onTokenPositionChange, previewTokenRotation]
  );


  const rotateTokenBy = useCallback(
    (tokenId, delta) => {
      if (!tokenId || !Number.isFinite(delta)) {
        return;
      }

      const tokensList = tokenPositionsRef.current;
      const overrides = rotationOverridesRef.current || {};
      const currentOverride = overrides[tokenId];
      const baseRotation = Number.isFinite(currentOverride)
        ? currentOverride
        : (() => {
            if (!Array.isArray(tokensList)) {
              return 0;
            }
            const foundToken = tokensList.find((entry) => entry?.characterId === tokenId);
            const rawRotation = Number(foundToken?.rotation);
            return Number.isFinite(rawRotation) ? rawRotation : 0;
          })();

      const nextRotation = normalizeDegrees(baseRotation + delta);
      if (!Number.isFinite(nextRotation)) {
        return;
      }

      applyTokenRotation(tokenId, nextRotation);
    },
    [applyTokenRotation]
  );

  const handleRotationHandlePointerDown = useCallback(
    (event, tokenId, currentRotation) => {
      if (interactionDisabled || !tokenId) {
        return;
      }

      if (typeof event?.button === 'number' && event.button !== 0) {
        return;
      }

      if (typeof event?.stopPropagation === 'function') {
        event.stopPropagation();
      }

      const tokenElement = event.currentTarget?.closest('.campaign-map-board__token');
      if (!tokenElement) {
        return;
      }

      const rect = tokenElement.getBoundingClientRect();
      if (!rect || !Number.isFinite(rect.left) || !Number.isFinite(rect.top)) {
        return;
      }

      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const pointerAngle = Math.atan2(event.clientY - centerY, event.clientX - centerX);
      const normalizedCurrent = normalizeDegrees(currentRotation);
      const initialHandleAngle = normalizeDegrees(normalizedCurrent + 90);

      if (tokenElement.style && typeof tokenElement.style.setProperty === 'function') {
        tokenElement.style.setProperty('--figurine-rotation', `${normalizedCurrent}deg`);
        tokenElement.style.setProperty('--rotation-handle-angle', `${initialHandleAngle}deg`);
      }

      stopRotationDrag();

      setDraggingRotationTokenId(tokenId);
      setHoveredTokenId(tokenId);

      rotationDragStateRef.current = {
        tokenId,
        pointerId: event.pointerId,
        pointerType: event.pointerType || null,
        centerX,
        centerY,
        tokenElement,
        baseRotationDegrees: normalizedCurrent,
        initialPointerAngle: pointerAngle,
        lastPointerAngle: pointerAngle,
        unwrappedPointerAngle: pointerAngle,
        pendingRotationDegrees: normalizedCurrent,
        handleElement: event.currentTarget || null,
      };

      if (
        event.currentTarget &&
        typeof event.currentTarget.setPointerCapture === 'function' &&
        event.pointerId !== undefined
      ) {
        try {
          event.currentTarget.setPointerCapture(event.pointerId);
        } catch (captureError) {
          // Ignore capture errors – some browsers might throw if capture isn't supported
        }
      }

      const handleRelease = (releaseEvent) => {
        const dragState = rotationDragStateRef.current;
        if (!dragState || dragState.tokenId !== tokenId) {
          stopRotationDrag();
          return;
        }

        if (
          dragState.pointerId !== undefined &&
          releaseEvent.pointerId !== undefined &&
          releaseEvent.pointerId !== dragState.pointerId
        ) {
          return;
        }

        const { pendingRotationDegrees, baseRotationDegrees } = dragState;

        const rotationToCommit = Number.isFinite(pendingRotationDegrees)
          ? pendingRotationDegrees
          : Number.isFinite(baseRotationDegrees)
            ? baseRotationDegrees
            : normalizedCurrent;

        applyTokenRotation(tokenId, rotationToCommit);

        if (dragState.tokenElement && typeof dragState.tokenElement.getBoundingClientRect === 'function') {
          const rect = dragState.tokenElement.getBoundingClientRect();
          if (
            rect &&
            Number.isFinite(rect.left) &&
            Number.isFinite(rect.top) &&
            Number.isFinite(rect.right) &&
            Number.isFinite(rect.bottom)
          ) {
            const isPointerInside =
              releaseEvent.clientX >= rect.left &&
              releaseEvent.clientX <= rect.right &&
              releaseEvent.clientY >= rect.top &&
              releaseEvent.clientY <= rect.bottom;

            setHoveredTokenId((prev) => {
              if (isPointerInside) {
                return tokenId;
              }
              return prev === tokenId ? null : prev;
            });
          }
        }

        releaseEvent.preventDefault();
        releaseEvent.stopPropagation();
        stopRotationDrag();
      };

      const handleMove = (moveEvent) => {
        const dragState = rotationDragStateRef.current;
        if (!dragState || dragState.tokenId !== tokenId) {
          return;
        }

        if (
          dragState.pointerId !== undefined &&
          moveEvent.pointerId !== undefined &&
          moveEvent.pointerId !== dragState.pointerId
        ) {
          return;
        }

        const pointerType = dragState.pointerType;
        if (
          pointerType &&
          (pointerType === 'mouse' || pointerType === 'pen') &&
          typeof moveEvent.buttons === 'number' &&
          (moveEvent.buttons & 1) === 0
        ) {
          handleRelease(moveEvent);
          return;
        }

        moveEvent.preventDefault();
        moveEvent.stopPropagation();

        let centerX = dragState.centerX;
        let centerY = dragState.centerY;

        if (
          dragState.tokenElement &&
          typeof dragState.tokenElement.getBoundingClientRect === 'function'
        ) {
          const updatedRect = dragState.tokenElement.getBoundingClientRect();
          if (
            updatedRect &&
            Number.isFinite(updatedRect.left) &&
            Number.isFinite(updatedRect.top)
          ) {
            centerX = updatedRect.left + updatedRect.width / 2;
            centerY = updatedRect.top + updatedRect.height / 2;
            dragState.centerX = centerX;
            dragState.centerY = centerY;
          }
        }

        const angle = Math.atan2(moveEvent.clientY - centerY, moveEvent.clientX - centerX);
        const { lastPointerAngle = angle, unwrappedPointerAngle = lastPointerAngle } = dragState;
        let delta = angle - lastPointerAngle;
        if (delta > Math.PI) {
          delta -= TWO_PI;
        } else if (delta < -Math.PI) {
          delta += TWO_PI;
        }

        const nextUnwrappedAngle = unwrappedPointerAngle + delta;
        dragState.lastPointerAngle = angle;
        dragState.unwrappedPointerAngle = nextUnwrappedAngle;

        const initialPointerAngle = dragState.initialPointerAngle ?? nextUnwrappedAngle;
        const baseRotationDegrees = dragState.baseRotationDegrees ?? 0;
        const deltaRadians = nextUnwrappedAngle - initialPointerAngle;
        const nextRotation = normalizeDegrees(
          baseRotationDegrees + radiansToDegrees(deltaRadians)
        );
        const handleAngle = normalizeDegrees(nextRotation + 90);

        if (
          dragState.tokenElement &&
          dragState.tokenElement.style &&
          typeof dragState.tokenElement.style.setProperty === 'function'
        ) {
          dragState.tokenElement.style.setProperty('--figurine-rotation', `${nextRotation}deg`);
          dragState.tokenElement.style.setProperty('--rotation-handle-angle', `${handleAngle}deg`);
        }

        dragState.pendingRotationDegrees = nextRotation;

        previewTokenRotation(tokenId, nextRotation);
      };

      const handleCancel = (cancelEvent) => {
        const dragState = rotationDragStateRef.current;
        if (
          dragState &&
          dragState.pointerId !== undefined &&
          cancelEvent.pointerId !== undefined &&
          cancelEvent.pointerId !== dragState.pointerId
        ) {
          return;
        }

        if (dragState && Number.isFinite(dragState.baseRotationDegrees)) {
          previewTokenRotation(tokenId, dragState.baseRotationDegrees);
        }

        setHoveredTokenId((prev) => (prev === tokenId ? null : prev));
        stopRotationDrag();
      };

      const usePointerEventListeners =
        pointerEventsSupported ||
        (typeof event?.type === 'string' && event.type.startsWith('pointer'));

      if (usePointerEventListeners) {
        rotationMoveHandlerRef.current = handleMove;
        rotationUpHandlerRef.current = handleRelease;
        rotationCancelHandlerRef.current = handleCancel;
      } else {
        rotationMoveHandlerRef.current = {
          mouse: (ev) => handleMove(enhanceMouseEvent(ev)),
          touch: (ev) => handleMove(enhanceTouchEvent(ev)),
        };
        rotationUpHandlerRef.current = {
          mouse: (ev) => handleRelease(enhanceMouseEvent(ev)),
          touch: (ev) => handleRelease(enhanceTouchEvent(ev)),
        };
        rotationCancelHandlerRef.current = {
          mouse: (ev) => handleCancel(enhanceMouseEvent(ev)),
          touch: (ev) => handleCancel(enhanceTouchEvent(ev)),
        };
      }

      const targets = [];
      if (typeof window !== 'undefined') {
        targets.push(window);
      }
      if (typeof document !== 'undefined') {
        targets.push(document);
      }

      targets.forEach((target) => {
        if (target && typeof target.addEventListener === 'function') {
          if (usePointerEventListeners) {
            target.addEventListener('pointermove', handleMove, { passive: false });
            target.addEventListener('pointerup', handleRelease, { passive: false });
            target.addEventListener('pointercancel', handleCancel, { passive: false });
          } else {
            const moveHandlers = rotationMoveHandlerRef.current;
            const upHandlers = rotationUpHandlerRef.current;
            const cancelHandlers = rotationCancelHandlerRef.current;

            if (moveHandlers?.mouse) {
              target.addEventListener('mousemove', moveHandlers.mouse, {
                passive: false,
              });
            }
            if (moveHandlers?.touch) {
              target.addEventListener('touchmove', moveHandlers.touch, {
                passive: false,
              });
            }

            if (upHandlers?.mouse) {
              target.addEventListener('mouseup', upHandlers.mouse, {
                passive: false,
              });
            }
            if (upHandlers?.touch) {
              target.addEventListener('touchend', upHandlers.touch, {
                passive: false,
              });
            }

            if (cancelHandlers?.mouse) {
              target.addEventListener('mouseleave', cancelHandlers.mouse, {
                passive: false,
              });
              target.addEventListener('mouseout', cancelHandlers.mouse, {
                passive: false,
              });
            }
            if (cancelHandlers?.touch) {
              target.addEventListener('touchcancel', cancelHandlers.touch, {
                passive: false,
              });
            }
          }
        }
      });

      setLastDraggedTokenId(tokenId);

      event.preventDefault();
      event.stopPropagation();
    },
    [
      applyTokenRotation,
      interactionDisabled,
      pointerEventsSupported,
      previewTokenRotation,
      setDraggingRotationTokenId,
      setHoveredTokenId,
      stopRotationDrag,
    ]
  );

  const lockRotation = useCallback((tokenId) => {
    if (!tokenId) {
      setLastDraggedTokenId(null);
      return;
    }

    setLastDraggedTokenId(tokenId);
  }, []);

  // While a sticker is chosen, the turning keys are its own.
  useTokenRotationShortcuts(editingStickers && chosenStickerId ? null : lastDraggedTokenId, rotateTokenBy, lockRotation, ROTATION_STEP_DEGREES);
  const chosenSticker = editingStickers ? (stickerMoves.layout?.stickers || []).find(({ id }) => id === chosenStickerId) || null : null;
  const turnChosenSticker = (step) => stickerMoves.move(chosenSticker.id, { x: chosenSticker.x, y: chosenSticker.y, turn: chosenSticker.turn + step });
  const stickerControls = canMoveStickers ? (
    <span className="map-sticker-controls">
      <button type="button" aria-pressed={editingStickers} onClick={() => {
        setMovingStickers(!editingStickers); setChosenStickerId(null);
        if (!editingStickers) { setFogPainting(false); setLastDraggedTokenId(null); }
      }}>{editingStickers ? 'Done moving stickers' : 'Move stickers'}</button>
      {chosenSticker && <>
        <button type="button" aria-label="Turn sticker left" title="Turn left (Q)" onClick={() => turnChosenSticker(-STICKER_TURN_STEP)}>↺</button>
        <button type="button" aria-label="Turn sticker right" title="Turn right (E)" onClick={() => turnChosenSticker(STICKER_TURN_STEP)}>↻</button>
      </>}
      {stickerMoves.error && <span role="status">{stickerMoves.error}</span>}
    </span>
  ) : null;

  return (
    <div
      ref={boardRef}
      className={classNames(
        'campaign-map-board',
        className,
        interactionDisabled && 'campaign-map-board--disabled',
        targeting && 'campaign-map-board--targeting',
        fog.active && 'campaign-map-board--fog'
      )}
      style={boardStyle}
      onClickCapture={event => {
        if (!fog.active || fog.canEdit || !(targeting || areaTargeting) || event.target.closest?.('.map-fog-controls')) return;
        const point = getNormalizedCoordinates(event.clientX, event.clientY);
        if (point && !revealed(fog.mask, point.x, point.y)) { event.preventDefault(); event.stopPropagation(); }
      }}
      onPointerDownCapture={event => { if (combatShortcuts.menu && !event.target.closest?.('[data-combat-unit-menu]')) combatShortcuts.close(); }}
      onWheel={event => { combatShortcuts.cancel(); combatShortcuts.close(); handleWheel(event); }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      {combatShortcuts.menu && createPortal(
        <div data-combat-unit-menu role="menu" aria-label={`${combatShortcuts.menu.label} combat options`}
          style={{ position: 'fixed', left: Math.max(8, Math.min(combatShortcuts.menu.x, window.innerWidth - 210)), top: Math.max(8, Math.min(combatShortcuts.menu.y, window.innerHeight - 100)), zIndex: 1070, background: '#20252b', color: 'white', border: '1px solid #87909b', borderRadius: 6, padding: 8, boxShadow: '0 3px 12px #0008' }}
          onPointerDown={event => event.stopPropagation()} onClick={event => event.stopPropagation()} onContextMenu={event => event.preventDefault()}>
          <button autoFocus type="button" role="menuitem" className="btn btn-sm btn-outline-light" disabled={combatShortcuts.busy}
            onClick={() => void combatShortcuts.toggle(combatShortcuts.menu.id)}>
            {(combatState?.participants || []).some(p => String(p.characterId || p.enemyId) === combatShortcuts.menu.id) ? 'Remove from Combat' : 'Add to Combat'}
          </button>
          <button type="button" aria-label="Close unit combat menu" className="btn btn-sm text-light ms-1" onClick={combatShortcuts.close}>×</button>
        </div>, document.body)}
      <FogControls fog={fog} painting={fogPainting} setPainting={enabled => { if (enabled && repositionToken) return; setFogPainting(enabled); if (enabled) { setLastDraggedTokenId(null); setHoveredTokenId(null); setMovingStickers(false); } }} size={fogBrushSize} setSize={setFogBrushSize} mode={fogBrushMode} setMode={setFogBrushMode}>{fogView === 'dm' && (placementControls || stickerControls) ? <>{placementControls}{stickerControls}</> : null}</FogControls>
      {title && <h5 className="campaign-map-board__title">{title}</h5>}
      {imageSrc ? (
        <div
          className={classNames(
            'campaign-map-board__stage',
            isMapPanning && 'campaign-map-board__stage--panning'
          )}
          style={panStyle}
        >
          <div className="campaign-map-board__image-wrapper">
            <img
              src={imageSrc}
              alt={altText}
              className="campaign-map-board__image"
              onLoad={handleMapImageLoad}
            />
            <div className="campaign-map-board__grid-overlay" aria-hidden="true" />
            <MapStickers map={map} layout={stickerMoves.layout} editing={editingStickers} selectedId={chosenStickerId}
              onSelect={setChosenStickerId} onMove={stickerMoves.move} />
            <div
              className={classNames(
                'campaign-map-board__tokens-layer',
                isMapPanning && 'campaign-map-board__tokens-layer--panning'
              )}
              ref={handleLayerRef}
              onPointerDown={handleLayerPointerDown}
              onPointerMove={handleLayerPointerMove}
              onPointerUp={handleLayerPointerUp}
              onPointerCancel={handleLayerPointerCancel}
              onMouseDown={(event) => {
                if (pointerEventsSupported) {
                  return;
                }
                handleLayerPointerDown(enhanceMouseEvent(event));
              }}
              onMouseMove={(event) => {
                if (pointerEventsSupported) {
                  return;
                }
                handleLayerPointerMove(enhanceMouseEvent(event));
              }}
              onMouseUp={(event) => {
                if (pointerEventsSupported) {
                  return;
                }
                handleLayerPointerUp(enhanceMouseEvent(event));
              }}
              onMouseLeave={(event) => {
                if (pointerEventsSupported) {
                  return;
                }
                handleLayerPointerCancel(enhanceMouseEvent(event));
              }}
              onTouchStart={(event) => {
                if (pointerEventsSupported) {
                  return;
                }
                handleLayerPointerDown(enhanceTouchEvent(event));
              }}
              onTouchMove={(event) => {
                if (pointerEventsSupported) {
                  return;
                }
                handleLayerPointerMove(enhanceTouchEvent(event));
              }}
              onTouchEnd={(event) => {
                if (pointerEventsSupported) {
                  return;
                }
                handleLayerPointerUp(enhanceTouchEvent(event));
              }}
              onTouchCancel={(event) => {
                if (pointerEventsSupported) {
                  return;
                }
                handleLayerPointerCancel(enhanceTouchEvent(event));
              }}
            >
              <svg aria-label="Reachable movement squares" viewBox={`0 0 ${gridColumns} ${gridRows}`} preserveAspectRatio="none" style={{position:'absolute',inset:0,width:'100%',height:'100%',pointerEvents:'none',zIndex:0}}>
                {movementCells.map(cell=><rect key={`${cell.point.x},${cell.point.y}`} x={cell.point.x} y={cell.point.y} width="1" height="1" strokeWidth=".03" data-movement-cell={cell.difficult?'difficult':'normal'}
                  fill={cell.difficult?'rgba(250,204,21,.28)':'rgba(60,180,220,.22)'} stroke={cell.difficult?'rgba(250,204,21,.75)':'rgba(70,200,240,.5)'}/>)}
                {movementPath?.length>1&&<polyline points={movementPath.map(p=>`${p.x+movementFootprintSize/2},${p.y+movementFootprintSize/2}`).join(' ')} fill="none" stroke="#ffc85a" strokeWidth=".12"/>}
              </svg>
              <PersistentSpellAreas effects={persistentEffects} map={areaMap} />
              <PaladinAuraAreas combatState={combatState} tokens={visibleTokenPositions} map={areaMap} />
              {rangePreview && <TargetRangePreview preview={rangePreview} hovered={previewOutcome} targetId={previewOutcome ? previewTargetId : null}
                tokens={visibleTokenPositions} gridColumns={gridColumns} gridRows={gridRows} />}
              {areaTargeting && <SpellAreaPreview targeting={areaTargeting} map={areaMap} tokens={areaTokens} allTokens={tokenPositions}
                sourceToken={sourceAreaToken} getCoordinates={getNormalizedCoordinates} barriers={areaStops}
                onPreviewChange={setAreaGeometry} onAreaSelected={onAreaSelected} />}
              {occupancyPreview && repositionToken && !disabled && (()=>{
                const layout=tokenFootprintLayout(repositionToken,occupancyPreview.position,gridColumns,gridRows);
                const image=resolveFigurineImageData(repositionToken).figurineImageUrl;
                return <div data-testid="placement-token-preview" aria-hidden="true" style={{position:'absolute',pointerEvents:'none',left:`${layout.center.x*100}%`,top:`${layout.center.y*100}%`,width:`${layout.width*100}%`,height:`${layout.height*100}%`,transform:'translate(-50%, -50%)',opacity:.65,zIndex:8,outline:occupancyPreview.valid?'2px solid #92d899':'2px solid #f66'}}>
                  {image?<img src={image} alt="" style={{width:'100%',height:'100%',objectFit:'contain',pointerEvents:'none'}}/>:<span style={{display:'grid',placeItems:'center',width:'100%',height:'100%',borderRadius:'50%',background:'#20344dcc',color:'white'}}>{repositionToken.label || 'Unit'}</span>}
                </div>;
              })()}
              {occupancyPreview && (
                <div
                  className={classNames(
                    'campaign-map-board__occupancy-preview',
                    !occupancyPreview.valid && 'campaign-map-board__occupancy-preview--invalid'
                  )}
                  data-testid="occupancy-preview"
                  aria-hidden="true"
                >
                  {occupancyPreview.cells.map((cell) => (
                    <span
                      key={`${cell.x}:${cell.y}`}
                      className="campaign-map-board__occupancy-cell"
                      data-grid-cell={`${cell.x},${cell.y}`}
                      style={{
                        left: `${(cell.x / gridColumns) * 100}%`,
                        top: `${(cell.y / gridRows) * 100}%`,
                        width: `${100 / gridColumns}%`,
                        height: `${100 / gridRows}%`,
                      }}
                    />
                  ))}
                </div>
              )}
              {visibleTokenPositions.map((token, tokenIndex) => {
                const {
                  characterId,
                  position,
                  color,
                  label,
                  currentHp,
                  maxHp,
                  variant,
                  isActiveTurn,
                  size,
                } = token;
                const footprint=tokenFootprintLayout(token,position,gridColumns,gridRows);
                const draggable = !targeting && !interactionDisabled && token.isMovable !== false;
                const normalizedLabel = normalizeText(label);
                const displayLabel = normalizedLabel || normalizeText(characterId) || characterId;
                const isLabelActive = activeLabelTokenId === characterId;
                const numericCurrentHp = toFiniteNumberOrNull(currentHp);
                const numericMaxHp = toFiniteNumberOrNull(maxHp);
                const defeatedEnemy = (token.entityType === 'enemy' || String(variant).toLowerCase() === 'enemy') && numericCurrentHp !== null && numericCurrentHp <= 0;
                const hasHealth =
                  !defeatedEnemy && numericCurrentHp !== null && numericMaxHp !== null && numericMaxHp > 0;
                const safeCurrentHp = hasHealth ? Math.max(0, numericCurrentHp) : null;
                const safeMaxHp = hasHealth ? Math.max(0, numericMaxHp) : null;
                const ratio =
                  hasHealth && safeMaxHp && safeMaxHp > 0
                    ? Math.min(1, Math.max(0, safeCurrentHp / safeMaxHp))
                    : 0;
                const fillPercent = Math.round(ratio * 10000) / 100;
                const healthColor = getHealthColor(ratio);
                const normalizedVariant =
                  typeof variant === 'string' && variant.trim() !== ''
                    ? variant.trim().toLowerCase()
                    : null;

                const { figurineImageUrl, figurineImagePublicId } = resolveFigurineImageData(token);
                const hasFigurineImage = Boolean(figurineImageUrl);
                const customArtScale = customTokenScales[figurineImageUrl] ?? customTokenScales[figurineImagePublicId];
                const figurineMetricKey = figurineImageUrl || characterId || `token-${tokenIndex}`;
                const metrics = figurineMetricKey ? figurineImageMetrics[figurineMetricKey] : null;
                const artworkOffset=artworkCenterOffset(metrics?.artwork);
                const hasMetadataSquareSize =
                  Number.isFinite(metadataSquareSize) && metadataSquareSize > 0;
                const imageFootprint =
                  hasFigurineImage && hasMetadataSquareSize
                    ? resolveFigurineSquaresFromImageSize(metrics, metadataSquareSize)
                    : null;
                const sizeKey = resolveFigurineSizeKey(size);
                const hasExplicitSize = typeof size === 'string' && size.trim() !== '';
                const baseFigurineScale =
                  FIGURINE_SIZE_MULTIPLIERS[sizeKey] ?? DEFAULT_FIGURINE_GRID_SQUARES;
                const figurineScale = (() => {
                  if (!hasExplicitSize && Number.isFinite(imageFootprint) && imageFootprint > 0) {
                    return imageFootprint;
                  }

                  if (Number.isFinite(baseFigurineScale) && baseFigurineScale > 0) {
                    return baseFigurineScale;
                  }

                  if (sizeKey === 'medium') {
                    return 1;
                  }

                  return DEFAULT_FIGURINE_GRID_SQUARES;
                })();
                const rotationHandleDistanceScale =
                  resolveRotationHandleDistanceScale(figurineScale);

                const figurineColor =
                  normalizedVariant === 'enemy'
                    ? dispositionColor(token)
                    : normalizeText(color) || undefined;
                const resolvedRotation = getResolvedRotationForToken(token);
                const rotationValue = Number.isFinite(resolvedRotation)
                  ? resolvedRotation
                  : 0;
                const rotationDisplay = Math.round(rotationValue * 10) / 10;
                const rotationStyleValue = `${rotationValue}deg`;
                const rotationHandleStyleValue = `${normalizeDegrees(rotationValue + 90)}deg`;
                const isRotationActive = lastDraggedTokenId === characterId;
                const isRotationHovered = hoveredTokenId === characterId;
                const isRotationDragging = draggingRotationTokenId === characterId;
                const isRotationVisible =
                  isRotationActive || isRotationHovered || isRotationDragging;

                const labelClassName = classNames(
                  'campaign-map-board__figurine-label',
                  (token.entityType === 'enemy' || normalizedVariant === 'enemy')
                    ? `campaign-map-board__figurine-label--${dispositionOf({ ...token, entityType: 'enemy' })}`
                    : normalizedVariant ? `campaign-map-board__figurine-label--${normalizedVariant}` : null
                );

                return (
                  <div
                    key={characterId}
                    role={draggable ? 'button' : undefined}
                    tabIndex={draggable ? 0 : -1}
                    aria-label={displayLabel}
                    className={classNames(
                      'campaign-map-board__token',
                      draggable && 'campaign-map-board__token--draggable',
                      isLabelActive && 'campaign-map-board__token--label-active',
                      hoveredTokenId === characterId &&
                        'campaign-map-board__token--hovered',
                      isActiveTurn && 'campaign-map-board__token--active-turn',
                      `campaign-map-board__token--size-${sizeKey}`,
                      isRotationActive && 'lastDragged',
                      isRotationVisible && 'campaign-map-board__token--rotation-visible',
                      isRotationDragging && 'campaign-map-board__token--rotation-active'
                      , targeting && numericCurrentHp > 0 && 'campaign-map-board__token--targetable'
                      , previewOutcome && previewTargetId === characterId && `campaign-map-board__token--preview campaign-map-board__token--preview-${previewOutcome.tone}`
                    )}
                    style={{
                      left: `${footprint.center.x * 100}%`,
                      top: `${footprint.center.y * 100}%`,
                      width: `${footprint.width * 100}%`,
                      height: `${footprint.height * 100}%`,
                      '--artwork-offset-x': `${artworkOffset.x}%`,
                      '--artwork-offset-y': `${artworkOffset.y}%`,
                      outline: affectedAreaIds.includes(characterId) ? '3px solid #ffb347' : undefined,
                      '--figurine-size-scale': figurineScale,
                      '--figurine-rotation': rotationStyleValue,
                      '--rotation-handle-distance-scale': rotationHandleDistanceScale,
                      '--rotation-handle-angle': rotationHandleStyleValue,
                    }}
                    title={displayLabel || undefined}
                    onClick={(event) => {
                      if (combatShortcuts.blockClick(event, characterId)) return;
                      if (targeting) {
                        event.preventDefault();
                        event.stopPropagation();
                        if (onTokenClick) {
                          onTokenClick(characterId, token);
                        }
                        return;
                      }
                      if (onTokenClick) {
                        event.stopPropagation();
                        onTokenClick(characterId, token);
                      }
                    }}
                    onAuxClick={event => { if (event.button === 1 && fogView === 'dm' && onToggleCombat) { event.preventDefault(); event.stopPropagation(); } }}
                    onPointerEnter={() => { if (rangePreview && characterId) setPreviewTargetId(characterId); }}
                    onPointerLeave={() => { if (rangePreview) setPreviewTargetId((current) => (current === characterId ? null : current)); }}
                    onPointerDown={(event) => {
                      if (combatShortcuts.down(event, token)) return;
                      if (characterId) {
                        setActiveLabelTokenId(characterId);
                      }
                      handlePointerDown(event, token);
                    }}
                    onPointerMove={event => { if (!combatShortcuts.move(event)) handlePointerMove(event); }}
                    onPointerUp={(event) => {
                      if (combatShortcuts.up(event)) return;
                      setActiveLabelTokenId((prev) =>
                        prev === characterId ? null : prev
                      );
                      handlePointerUp(event);
                    }}
                    onPointerCancel={(event) => {
                      if (combatShortcuts.up(event, true)) return;
                      setActiveLabelTokenId((prev) =>
                        prev === characterId ? null : prev
                      );
                      handlePointerCancel(event);
                    }}
                    onPointerOver={() => {
                      if (!interactionDisabled && characterId) {
                        setHoveredTokenId(characterId);
                        setLastDraggedTokenId((prev) =>
                          prev && prev !== characterId ? null : prev
                        );
                      }
                    }}
                    onPointerEnter={() => {
                      if (!interactionDisabled && characterId) {
                        setHoveredTokenId(characterId);
                        setLastDraggedTokenId((prev) =>
                          prev && prev !== characterId ? null : prev
                        );
                      }
                    }}
                    onPointerLeave={(event) => {
                      if (
                        draggingRotationTokenId === characterId ||
                        (event?.relatedTarget?.nodeType &&
                          event.currentTarget &&
                          event.currentTarget.contains(event.relatedTarget))
                      ) {
                        return;
                      }
                      setHoveredTokenId((prev) => (prev === characterId ? null : prev));
                    }}
                    onMouseDown={(event) => {
                      if (event.button === 1 && fogView === 'dm' && onToggleCombat) {
                        event.preventDefault(); event.stopPropagation();
                        if (!pointerEventsSupported) combatShortcuts.down(enhanceMouseEvent(event), token);
                        return;
                      }
                      if (pointerEventsSupported) {
                        return;
                      }
                      const adapted = enhanceMouseEvent(event);
                      if (characterId) {
                        setActiveLabelTokenId(characterId);
                      }
                      handlePointerDown(adapted, token);
                    }}
                    onMouseMove={(event) => {
                      if (pointerEventsSupported) {
                        return;
                      }
                      handlePointerMove(enhanceMouseEvent(event));
                    }}
                    onMouseUp={(event) => {
                      if (pointerEventsSupported) {
                        return;
                      }
                      const adapted = enhanceMouseEvent(event);
                      setActiveLabelTokenId((prev) =>
                        prev === characterId ? null : prev
                      );
                      handlePointerUp(adapted);
                    }}
                    onMouseLeave={(event) => {
                      if (rangePreview) setPreviewTargetId((current) => (current === characterId ? null : current));
                      if (pointerEventsSupported) {
                        return;
                      }
                      if (
                        draggingRotationTokenId === characterId ||
                        (event?.relatedTarget?.nodeType &&
                          event.currentTarget &&
                          event.currentTarget.contains(event.relatedTarget))
                      ) {
                        return;
                      }
                      setHoveredTokenId((prev) => (prev === characterId ? null : prev));
                      handlePointerCancel(enhanceMouseEvent(event));
                    }}
                    onMouseOver={() => {
                      if (pointerEventsSupported) {
                        return;
                      }
                      if (!interactionDisabled && characterId) {
                        setHoveredTokenId(characterId);
                        setLastDraggedTokenId((prev) =>
                          prev && prev !== characterId ? null : prev
                        );
                      }
                    }}
                    onMouseEnter={() => {
                      if (rangePreview && characterId) setPreviewTargetId(characterId);
                      if (pointerEventsSupported) {
                        return;
                      }
                      if (!interactionDisabled && characterId) {
                        setHoveredTokenId(characterId);
                        setLastDraggedTokenId((prev) =>
                          prev && prev !== characterId ? null : prev
                        );
                      }
                    }}
                    onTouchStart={(event) => {
                      if (pointerEventsSupported) {
                        return;
                      }
                      const adapted = enhanceTouchEvent(event);
                      if (combatShortcuts.down(adapted, token)) return;
                      if (characterId) {
                        setActiveLabelTokenId(characterId);
                        setHoveredTokenId(characterId);
                      }
                      handlePointerDown(adapted, token);
                    }}
                    onTouchMove={(event) => {
                      if (pointerEventsSupported) {
                        return;
                      }
                      const adapted = enhanceTouchEvent(event);
                      if (!combatShortcuts.move(adapted)) handlePointerMove(adapted);
                    }}
                    onTouchEnd={(event) => {
                      if (pointerEventsSupported) {
                        return;
                      }
                      const adapted = enhanceTouchEvent(event);
                      if (combatShortcuts.up(adapted)) return;
                      setActiveLabelTokenId((prev) =>
                        prev === characterId ? null : prev
                      );
                      setHoveredTokenId((prev) => (prev === characterId ? null : prev));
                      handlePointerUp(adapted);
                    }}
                    onTouchCancel={(event) => {
                      if (pointerEventsSupported) {
                        return;
                      }
                      const adapted = enhanceTouchEvent(event);
                      if (combatShortcuts.up(adapted, true)) return;
                      setActiveLabelTokenId((prev) =>
                        prev === characterId ? null : prev
                      );
                      setHoveredTokenId((prev) => (prev === characterId ? null : prev));
                      handlePointerCancel(adapted);
                    }}
                    onContextMenu={(event) => {
                      if (combatShortcuts.context(event, characterId)) { event.preventDefault(); event.stopPropagation(); return; }
                      event.preventDefault();
                      event.stopPropagation();
                      if (
                        interactionDisabled ||
                        typeof onTokenRemove !== 'function' ||
                        !characterId
                      ) {
                        return;
                      }

                      onTokenRemove({
                        characterId,
                        token,
                        mapId:
                          typeof safeMap?.mapId === 'string' && safeMap.mapId.trim() !== ''
                            ? safeMap.mapId.trim()
                            : null,
                      });
                    }}
                    data-disposition={dispositionOf(token)}
                    data-token-id={characterId}
                    data-rotation={rotationValue}
                  >
                    {displayLabel && (
                      <span className={labelClassName} aria-hidden="true">
                        {displayLabel}
                      </span>
                    )}
                    {isActiveTurn && (
                      <span className="campaign-map-board__turn-indicator">
                        <span aria-hidden="true">!</span>
                        <span className="visually-hidden">
                          {`${displayLabel || 'This character'} is taking their turn`}
                        </span>
                      </span>
                    )}
                    <div className="campaign-map-board__token-figure">
                      {hasHealth && safeCurrentHp !== null && safeMaxHp !== null && safeMaxHp > 0 && (
                        <div
                          className="campaign-map-board__health"
                          style={{ '--campaign-map-board-health-color': healthColor }}
                        >
                          <div className="campaign-map-board__health-content">
                            <span className="visually-hidden">{`HP: ${formatHpValue(
                              safeCurrentHp
                            )}/${formatHpValue(safeMaxHp)}`}</span>
                            <div className="campaign-map-board__health-track">
                              <div
                                className="campaign-map-board__health-fill"
                                style={{ width: `${Math.max(0, Math.min(100, fillPercent))}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="campaign-map-board__figurine-rotation-wrapper">
                        <div
                          className={classNames(
                            'campaign-map-board__figurine',
                            draggable && 'campaign-map-board__figurine--active',
                            isActiveTurn && 'campaign-map-board__figurine--active-turn',
                            hasFigurineImage && 'campaign-map-board__figurine--has-image'
                          )}
                          style={{ '--figurine-color': figurineColor, opacity: defeatedEnemy ? .6 : undefined }}
                        >
                          {hasFigurineImage && (
                            <span className="campaign-map-board__figurine-image-wrapper" aria-hidden="true">
                              <img
                                src={figurineImageUrl}
                                alt=""
                                className={
                                  figurineImageUrl.includes("Scale300")
                                  || figurineImageUrl.includes("x33_01")
                                  || figurineImageUrl.includes("x18_01")
                                  ?"campaign-map-board__figurine-image__scale-up-300"
                                  :figurineImageUrl.includes("Scale133")
                                  ?"campaign-map-board__figurine-image__scale-up-133"
                                  :figurineImageUrl.includes("Scale150")
                                  ?"campaign-map-board__figurine-image__scale-up-150"
                                  :figurineImageUrl.includes("Scale166")
                                  ?"campaign-map-board__figurine-image__scale-up-166"
                                  :figurineImageUrl.includes("Scale200")
                                  ?"campaign-map-board__figurine-image__scale-up-200"
                                  :figurineImageUrl.includes("Scale233")
                                  ?"campaign-map-board__figurine-image__scale-up-233"
                                  :figurineImageUrl.includes("Scale333")
                                  ?"campaign-map-board__figurine-image__scale-up-333"
                                  :"campaign-map-board__figurine-image"
                                }
                                style={customArtScale ? { '--figurine-image-scale': customArtScale } : undefined}
                                data-figurine-public-id={figurineImagePublicId || undefined}
                                loading="lazy"
                                onLoad={(event) =>
                                  handleFigurineImageLoad(
                                    figurineMetricKey,
                                    event?.currentTarget || event?.target || null
                                  )
                                }
                              />
                            </span>
                          )}
                          <span className="campaign-map-board__figurine-figure" aria-hidden="true">
                            <span className="campaign-map-board__figurine-head" />
                            <span className="campaign-map-board__figurine-torso" />
                            <span className="campaign-map-board__figurine-cloak" />
                          </span>
                        </div>
                      </div>
                    </div>
                    {isRotationVisible && (
                      <div
                        className="campaign-map-board__rotation-controls"
                        style={{ '--rotation-handle-angle': rotationHandleStyleValue }}
                        onPointerDown={(event) => event.stopPropagation()}
                        onPointerMove={(event) => {
                          if (!isRotationDragging) {
                            event.stopPropagation();
                          }
                        }}
                        onPointerUp={(event) => {
                          if (!isRotationDragging) {
                            event.stopPropagation();
                          }
                        }}
                        onMouseDown={(event) => {
                          if (!pointerEventsSupported) {
                            event.stopPropagation();
                          }
                        }}
                        onMouseMove={(event) => {
                          if (!pointerEventsSupported && !isRotationDragging) {
                            event.stopPropagation();
                          }
                        }}
                        onMouseUp={(event) => {
                          if (!pointerEventsSupported && !isRotationDragging) {
                            event.stopPropagation();
                          }
                        }}
                        onTouchStart={(event) => {
                          if (!pointerEventsSupported) {
                            event.stopPropagation();
                          }
                        }}
                        onTouchMove={(event) => {
                          if (!pointerEventsSupported && !isRotationDragging) {
                            event.stopPropagation();
                          }
                        }}
                        onTouchEnd={(event) => {
                          if (!pointerEventsSupported && !isRotationDragging) {
                            event.stopPropagation();
                          }
                        }}
                      >
                        <div className="campaign-map-board__rotation-handle-track">
                          <button
                            type="button"
                            className="campaign-map-board__rotation-handle"
                            aria-label={
                              Number.isFinite(rotationDisplay)
                                ? `Rotate figurine (current ${rotationDisplay}°)`
                                : 'Rotate figurine'
                            }
                            onPointerDown={(event) =>
                              handleRotationHandlePointerDown(event, characterId, rotationValue)
                            }
                            onPointerUp={(event) => {
                              event.stopPropagation();
                              lockRotation(characterId);
                            }}
                            onPointerCancel={(event) => {
                              event.stopPropagation();
                              stopRotationDrag();
                            }}
                            onFocus={() => setLastDraggedTokenId(characterId)}
                            onBlur={() =>
                              setLastDraggedTokenId((prev) => (prev === characterId ? null : prev))
                            }
                            onMouseDown={(event) => {
                              if (pointerEventsSupported) {
                                return;
                              }
                              handleRotationHandlePointerDown(
                                enhanceMouseEvent(event),
                                characterId,
                                rotationValue
                              );
                            }}
                            onMouseUp={(event) => {
                              if (pointerEventsSupported) {
                                return;
                              }
                              event.stopPropagation();
                              lockRotation(characterId);
                            }}
                            onMouseLeave={(event) => {
                              if (pointerEventsSupported) {
                                return;
                              }
                              event.stopPropagation();
                              stopRotationDrag();
                            }}
                            onTouchStart={(event) => {
                              if (pointerEventsSupported) {
                                return;
                              }
                              handleRotationHandlePointerDown(
                                enhanceTouchEvent(event),
                                characterId,
                                rotationValue
                              );
                            }}
                            onTouchEnd={(event) => {
                              if (pointerEventsSupported) {
                                return;
                              }
                              event.stopPropagation();
                              lockRotation(characterId);
                            }}
                            onTouchCancel={(event) => {
                              if (pointerEventsSupported) {
                                return;
                              }
                              event.stopPropagation();
                              stopRotationDrag();
                            }}
                          >
                            <svg
                              aria-hidden="true"
                              className="campaign-map-board__rotation-handle-icon"
                              viewBox="0 0 48 48"
                              focusable="false"
                            >
                              <path
                                d="M24 8A16 16 0 1 0 36.5 12.5"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M36.5 12.5H45V21"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M45 12.5 38 19.5"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M45 12.5 38 5.5"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <MapCanopy map={map} />
            <FogOfWar fog={fog} painting={fogPainting} size={fogBrushSize} mode={fogBrushMode} columns={gridColumns} rows={gridRows} />
          </div>
          {children}
        </div>
      ) : (
        <p className="text-muted mb-0">No map image available.</p>
      )}
    </div>
  );
};

CampaignMapBoard.propTypes = {
  campaignId: PropTypes.string,
  fogView: PropTypes.oneOf(['dm', 'player']),
  map: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  tokens: PropTypes.arrayOf(
    PropTypes.shape({
      characterId: PropTypes.string.isRequired,
      x: PropTypes.number,
      y: PropTypes.number,
      color: PropTypes.string,
      label: PropTypes.string,
      variant: PropTypes.oneOf(['enemy', 'ally', 'self']),
      isMovable: PropTypes.bool,
      movementLocked: PropTypes.bool,
      currentHp: PropTypes.number,
      maxHp: PropTypes.number,
      isActiveTurn: PropTypes.bool,
      size: PropTypes.string,
      figurineImageUrl: PropTypes.string,
      figurineImagePublicId: PropTypes.string,
      rotation: PropTypes.number,
    })
  ),
  onTokenDragStart: PropTypes.func,
  onTokenDrag: PropTypes.func,
  onTokenDragEnd: PropTypes.func,
  onTokenPositionChange: PropTypes.func,
  onBackgroundClick: PropTypes.func,
  onTokenRemove: PropTypes.func,
  onTokenClick: PropTypes.func,
  onToggleCombat: PropTypes.func,
  combatState: PropTypes.object,
  targeting: PropTypes.bool,
  // While choosing a target: the user's token, the outlines to draw around it, and evaluate(targetId) -> { tone, description }.
  rangePreview: PropTypes.shape({ sourceId: PropTypes.string, rings: PropTypes.arrayOf(PropTypes.object), evaluate: PropTypes.func }),
  areaTargeting: PropTypes.object,
  onAreaSelected: PropTypes.func,
  repositionToken: PropTypes.shape({ characterId: PropTypes.string, size: PropTypes.string }),
  disabled: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
  allowWheelZoom: PropTypes.bool,
};

CampaignMapBoard.defaultProps = {
  map: null,
  tokens: [],
  onTokenDragStart: null,
  onTokenDrag: null,
  onTokenDragEnd: null,
  onTokenPositionChange: null,
  onBackgroundClick: null,
  onTokenRemove: null,
  onTokenClick: null,
  targeting: false,
  repositionToken: null,
  disabled: false,
  className: '',
  children: null,
  allowWheelZoom: false,
};

export default CampaignMapBoard;

